import { aiDraftsAr } from './ai-drafts/ar';
import { automationsAr } from './automations/ar';
import { blockingAr } from './blocking/ar';
import { cachingAr } from './caching';
import { channelsAr } from './channels/ar';
import { chatWidgetAr } from './chat-widget/ar';
import { checksAr } from './checks';
import { commerceAr } from './commerce/ar';
import { decisionsAr } from './decisions';
import { developerAr } from './developer/ar';
import type { Translation } from './en';
import { offersAr } from './offers/ar';
import { privacyAr } from './privacy/ar';
import { reportsAr } from './reports/ar';
import { slaAr } from './sla/ar';
import { ticketsAr } from './tickets/ar';
import { toolsAr } from './tools';
import { chatSettingsAr } from './widget/ar';

/** Arabic (Modern Standard). Pending review by native speakers, as the note in the interface says. */
export const ar: Translation = {
  app: {
    name: 'Hamnivo Studio',
    skipToContent: 'تخطَّ إلى المحتوى',
    language: 'اللغة',
    translationNote: 'هذه الترجمة بانتظار مراجعة متحدثين أصليين.',
    close: 'إغلاق',
    cancel: 'إلغاء',
    retry: 'حاول مرة أخرى',
    reference: 'المرجع',
    copied: 'تم النسخ',
    optional: 'اختياري',
    serverDetail: 'من الخادم: {{detail}}',
    cancelled: 'أُلغي. لم يتغير شيء.',
    loading: 'جارٍ التحميل',
  },
  nav: {
    label: 'الاستوديو',
    home: 'الرئيسية',
    inbox: 'صندوق الوارد',
    ai: 'مساعد الذكاء الاصطناعي',
    sales: 'المبيعات',
    settings: 'الإعدادات',
    sections: 'صفحات {{name}}',
    brands: 'العلامات التجارية والتثبيت',
    agents: 'وكلاء الذكاء الاصطناعي',
    models: 'اتصالات النماذج',
    knowledge: 'المعرفة',
    memory: 'ذاكرة العملاء',
    runs: 'النشاط',
    team: 'الفريق والصلاحيات',
    security: 'الأمان',
    platform: 'واجهة المنصة',
    account: 'أمان الحساب',
    openMenu: 'فتح القائمة',
    menu: 'القائمة',
    switchWorkspace: 'تبديل مساحة العمل',
    workspace: 'مساحة العمل',
    signOut: 'تسجيل الخروج',
    signedInAs: 'مسجّل الدخول باسم',
  },
  errors: {
    network: {
      title: 'تعذّر الوصول إلى Hamnivo',
      description: 'تحقق من اتصالك وحاول مرة أخرى.',
    },
    denied: {
      title: 'ليست لديك صلاحية للقيام بذلك',
      description: 'اطلب الوصول من مالك مساحة العمل أو من مسؤول.',
    },
    notFound: {
      title: 'تعذّر العثور على هذا',
      description: 'ربما أُزيل، أو أن الرابط غير صحيح.',
    },
    validation: {
      title: 'بعض المعلومات تحتاج إلى مراجعة',
      description: 'تحقق من التفاصيل وحاول مرة أخرى.',
    },
    rateLimited: {
      title: 'طلبات كثيرة جدًا',
      description: 'انتظر قليلًا ثم حاول مرة أخرى.',
      retryIn: 'يمكنك المحاولة مرة أخرى بعد {{seconds}} ث.',
      retryNow: 'يمكنك المحاولة مرة أخرى الآن.',
    },
    unavailable: {
      title: 'خدمة متصلة لا تستجيب',
      description: 'لم يتغير شيء. حاول مرة أخرى بعد قليل.',
    },
    idempotency: {
      title: 'أُرسل هذا من قبل بتفاصيل مختلفة',
      description: 'أعد تحميل الصفحة لترى ما حُفظ.',
    },
    conflict: {
      title: 'غيّر شخص آخر هذا أولًا',
      description: 'أعد التحميل لترى أحدث نسخة.',
    },
    mfa: {
      title: 'التحقق بخطوتين مطلوب',
      description: 'فعّل التحقق بخطوتين من صفحة أمان الحساب، ثم حاول مرة أخرى.',
    },
    tooLarge: { title: 'الملف كبير جدًا', description: 'يمكن أن يصل حجم الملف إلى 5 ميغابايت.' },
    rejected: { title: 'رُفض الملف', description: 'تعذّر قبول الملف.' },
    disabled: {
      title: 'هذه الميزة متوقفة',
      description: 'اطلب من المسؤول تشغيلها.',
    },
    internal: {
      title: 'حدث خطأ من جهتنا',
      description: 'لم يتغير شيء. حاول مرة أخرى.',
    },
    planLimit: {
      title: 'تم بلوغ حد الخطة',
      description:
        'لا تتضمن خطتك المزيد من هذا. حرّر بعض الاستخدام، أو اسأل مالك مساحة العمل عن خطة أكبر.',
    },
    reauth: {
      title: 'سجّل الدخول مرة أخرى للمتابعة',
      description: 'لحمايتك، يتطلب هذا تسجيل دخول حديثًا.',
      action: 'تسجيل الدخول مرة أخرى',
    },
    channelWindow: {
      title: 'لا يمكن الآن إرسال إلا رسائل القوالب',
      description:
        'يسمح واتساب بالردّ خلال 24 ساعة من آخر رسالة كتبها العميل. لم يُرسَل شيء. يمكنك الردّ مجددًا بعد أن يكتب العميل.',
    },
    unknown: { title: 'حدث خطأ ما', description: 'حاول مرة أخرى.' },
    page: {
      title: 'تعذّر تحميل هذه الصفحة',
      description: 'حاول مرة أخرى. إذا تكرر ذلك، أعد تحميل الصفحة.',
    },
  },
  states: {
    stale: {
      since: 'تعذّر تحديث هذا. يُعرض ما حُمّل {{time}}.',
      unknown: 'تعذّر تحديث هذا. قد يكون ما تراه قديمًا.',
    },
    forbiddenTitle: 'ليس لديك وصول إلى هذه الصفحة',
    forbiddenDescription: 'اطلب من مالك مساحة العمل أو من مسؤول أن يمنحك الوصول.',
    backToOverview: 'العودة إلى النظرة العامة',
    notFoundTitle: 'الصفحة غير موجودة',
    notFoundDescription: 'ربما العنوان غير صحيح، أو نُقلت الصفحة.',
  },
  auth: {
    email: 'البريد الإلكتروني',
    password: 'كلمة المرور',
    showPassword: 'إظهار كلمة المرور',
    hidePassword: 'إخفاء كلمة المرور',
    errorSummary: 'صحّح ما يلي للمتابعة',
    emailRequired: 'أدخل بريدك الإلكتروني.',
    emailInvalid: 'أدخل بريدًا إلكترونيًا مثل name@example.com.',
    passwordRequired: 'أدخل كلمة المرور.',
    rateLimited: 'محاولات كثيرة جدًا. انتظر بضع دقائق ثم حاول مرة أخرى.',
    privacy:
      'يُبقيك Studio مسجّل الدخول عبر ملف تعريف ارتباط آمن. ما تكتبه يبقى في هذه النافذة حتى تحفظه.',
    signIn: {
      title: 'مرحبًا بعودتك',
      description: 'سجّل الدخول إلى Hamnivo Studio للمتابعة.',
      keepSignedIn: 'أبقني مسجّل الدخول على هذا الجهاز',
      keepSignedInHint: 'لا تحدد هذا الخيار على جهاز مشترك، وعندها يُسجَّل خروجك عند إغلاق المتصفح.',
      submit: 'تسجيل الدخول',
      or: 'أو',
      passkey: 'تسجيل الدخول بمفتاح المرور',
      forgot: 'هل نسيت كلمة المرور؟',
      noAccount: 'جديد على Hamnivo؟',
      createAccount: 'أنشئ حسابًا',
      failed: 'تعذّر تسجيل دخولك. تحقق من البريد الإلكتروني وكلمة المرور، أو أعد تعيين كلمة المرور.',
      notVerified: 'أكّد بريدك الإلكتروني أولًا. أرسلنا إليك رابطًا عند التسجيل.',
      resendVerification: 'أرسل الرابط مرة أخرى',
      verificationSent: 'أرسلنا رابطًا جديدًا. تفقّد بريدك.',
      passkeyFailed: 'لم ينجح مفتاح المرور. حاول مرة أخرى، أو سجّل الدخول بكلمة المرور.',
    },
    twoFactor: {
      title: 'تحقّق من هويتك',
      description: 'أدخل الرمز المكوّن من 6 أرقام من تطبيق المصادقة.',
      code: 'رمز من 6 أرقام',
      codeHint: 'يتغير الرمز كل 30 ثانية.',
      submit: 'تحقّق',
      useBackup: 'استخدم رمزًا احتياطيًا بدلًا من ذلك',
      useApp: 'استخدم رمزًا من التطبيق بدلًا من ذلك',
      backupCode: 'رمز احتياطي',
      backupHint: 'أحد الرموز التي حفظتها عند تفعيل التحقق بخطوتين.',
      codeRequired: 'أدخل الرمز.',
      invalid: 'لم ينجح هذا الرمز. تحقق منه وحاول مرة أخرى.',
      expired: 'انتهت صلاحية محاولة تسجيل الدخول هذه. سجّل الدخول مرة أخرى لبدء تحقق جديد.',
      tooMany: 'محاولات كثيرة جدًا. سجّل الدخول مرة أخرى للمتابعة.',
      back: 'العودة إلى تسجيل الدخول',
    },
    signUp: {
      title: 'أنشئ حسابك',
      description: 'بعد ذلك يمكنك إنشاء مساحة عمل أو الانضمام إلى مساحة دُعيت إليها.',
      name: 'اسمك',
      nameRequired: 'أدخل اسمك.',
      passwordHint: '12 حرفًا على الأقل. الجملة القصيرة سهلة التذكّر.',
      passwordShort: 'استخدم 12 حرفًا على الأقل.',
      passwordLong: 'استخدم 256 حرفًا على الأكثر.',
      submit: 'إنشاء الحساب',
      haveAccount: 'لديك حساب بالفعل؟',
      signIn: 'تسجيل الدخول',
      failed: 'تعذّر إنشاء هذا الحساب. إذا كان لديك حساب، سجّل الدخول أو أعد تعيين كلمة المرور.',
    },
    checkEmail: {
      title: 'تفقّد بريدك',
      verify: 'أرسلنا رابطًا إلى {{email}}. افتحه لتأكيد عنوانك وإكمال التسجيل.',
      reset:
        'إذا كان هناك حساب يستخدم {{email}}، فقد أرسلنا إليه رابطًا لاختيار كلمة مرور جديدة. يعمل الرابط لمدة ساعة واحدة.',
      help: 'لم تجده؟ تفقّد مجلد الرسائل غير المرغوب فيها، أو انتظر دقيقة وأرسله مرة أخرى.',
      resend: 'أرسل الرابط مرة أخرى',
      sent: 'أرسلنا رابطًا جديدًا.',
      back: 'العودة إلى تسجيل الدخول',
    },
    forgot: {
      title: 'أعد تعيين كلمة المرور',
      description: 'أدخل البريد الإلكتروني لحسابك. سنرسل رابطًا لاختيار كلمة مرور جديدة.',
      submit: 'أرسل رابط إعادة التعيين',
    },
    reset: {
      title: 'اختر كلمة مرور جديدة',
      description: 'يُسجَّل خروجك من جلساتك الأخرى عند تغييرها.',
      password: 'كلمة المرور الجديدة',
      submit: 'احفظ كلمة المرور الجديدة',
      invalidLink: 'رابط إعادة التعيين هذا غير صالح أو منتهي الصلاحية. اطلب رابطًا جديدًا.',
      requestNew: 'اطلب رابطًا جديدًا',
      done: 'تم تغيير كلمة المرور. سجّل الدخول بكلمة المرور الجديدة.',
    },
    reauth: {
      stepUpTitle: 'أكّد هويتك',
      stepUpDescription:
        'يتطلب هذا الإجراء تسجيل دخول حديثًا. أدخل كلمة المرور للمتابعة. يبقى عملك في هذه الصفحة كما هو.',
      expiredTitle: 'سجّل الدخول للمتابعة',
      expiredDescription:
        'انتهت جلستك. سجّل الدخول مرة أخرى للمتابعة. تبقى التغييرات غير المحفوظة في هذه الصفحة حتى تفعل ذلك.',
      account: 'الحساب',
      confirm: 'تأكيد',
      signInAgain: 'سجّل الدخول مرة أخرى',
      otherAccount: 'سجّل الدخول بحساب آخر',
      masked: 'محتوى مساحة العمل مخفي حتى تسجّل الدخول.',
    },
  },
  tenants: {
    title: 'اختر مساحة عمل',
    description:
      'تفصل مساحات العمل بين العلامات التجارية والأشخاص والمحادثات. يتذكر Studio اختيارك في هذا المتصفح.',
    yours: 'مساحات عملك',
    open: 'فتح',
    current: 'الحالية',
    unavailable: 'لا يمكنك فتح مساحة العمل هذه الآن.',
    status: {
      active: 'نشط',
      suspended: 'معلّق',
      closing: 'قيد الإغلاق',
      invited: 'بانتظار قبول الدعوة',
      removed: 'مُزال',
    },
    empty: {
      title: 'لا توجد مساحات عمل بعد',
      description: 'أنشئ واحدة للبدء، أو اطلب من زميل أن يدعوك.',
    },
    create: {
      title: 'أنشئ مساحة عمل',
      name: 'اسم مساحة العمل',
      nameHint: 'عادةً اسم شركتك أو فريقك.',
      slug: 'عنوان مساحة العمل',
      slugHint: 'من 3 إلى 40 حرفًا لاتينيًا صغيرًا أو رقمًا أو شرطة. يُستخدم في الروابط.',
      language: 'اللغة الافتراضية',
      submit: 'إنشاء مساحة العمل',
      nameRequired: 'أدخل اسمًا لمساحة العمل.',
      slugInvalid:
        'استخدم من 3 إلى 40 حرفًا لاتينيًا صغيرًا أو رقمًا أو شرطة، على أن يبدأ وينتهي بحرف أو رقم.',
      slugTaken: 'هذا العنوان مستخدم. جرّب عنوانًا آخر.',
      created: 'أُنشئت مساحة العمل.',
    },
    revoked: {
      title: 'تغيّر وصولك إلى مساحة العمل هذه',
      description: 'لم يعد بإمكانك فتح {{name}}. أُزيلت بياناتها من هذه النافذة.',
    },
  },
  shell: {
    aiStopped: 'الذكاء الاصطناعي متوقف',
    stopAi: 'إيقاف الذكاء الاصطناعي',
    stopAiLabel: 'إيقاف ردود الذكاء الاصطناعي',
    killBanner: {
      title: 'ردود الذكاء الاصطناعي متوقفة',
      people: 'يستمر الأشخاص في المحادثة. تذهب الرسائل الجديدة إلى فريقك.',
      review: 'مراجعة الإيقافات',
    },
    scope: {
      tenant: 'في كل مكان في مساحة العمل هذه',
      brand: 'العلامة التجارية: {{name}}',
      agent: 'الوكيل: {{name}}',
    },
    reason: 'السبب: {{reason}}',
    since: 'منذ {{time}}',
  },
  overview: {
    degraded: {
      title: 'تعذّر تحميل جزء من الصفحة الرئيسية',
      description: 'باقي Studio يعمل، ومنه صندوق الوارد. يمكن إعادة محاولة كل جزء أدناه وحده.',
    },
    title: 'نظرة عامة',
    greeting: 'مرحبًا بعودتك، {{name}}.',
    chats: {
      title: 'محادثاتك',
      description: 'العملاء الذين ينتظرون شخصًا، والمحادثات المسندة إليك.',
      loading: 'جارٍ تحميل محادثاتك',
      waiting: 'بانتظار شخص',
      mine: 'مسندة إليك',
      caughtUp: 'لا شيء ينتظرك الآن.',
      open: 'افتح صندوق الوارد',
    },
    setup: {
      title: 'ابدأ الإعداد',
      description: 'تبقّت هذه الخطوات قبل أن يتمكن العملاء من محادثة مساعد الذكاء الاصطناعي.',
      steps: {
        engine: {
          title: 'اربط محرك المحادثة',
          description: 'اربط علامة تجارية لتصل محادثاتها إلى فريقك.',
        },
        brand: {
          title: 'أضف علامة تجارية وثبّت المحادثة',
          description: 'أعطِها اسمًا، واسمح بموقعك، ثم ألصق رمز التثبيت فيه.',
        },
        agent: {
          title: 'انشر وكيل ذكاء اصطناعي',
          description: 'لا يجيب مساعدك العملاء إلا بعد أن تنشره.',
        },
        team: {
          title: 'ادعُ زميلًا',
          description: 'أضف من سيجيبون على المحادثات معك.',
        },
      },
    },
    brands: {
      title: 'العلامات التجارية',
      description: 'حالة اتصال المحادثة والذكاء الاصطناعي لكل علامة تجارية.',
      loading: 'جارٍ تحميل العلامات التجارية',
      columns: { chat: 'اتصال المحادثة', ai: 'مساعد الذكاء الاصطناعي' },
      install: 'التثبيت',
      openAgent: 'فتح الوكيل',
      empty: {
        title: 'لا توجد علامات تجارية بعد',
        description: 'لكل علامة تجارية أداة محادثة خاصة بها ومواقع مسموح بها ومساعد ذكاء اصطناعي.',
        action: 'أنشئ علامة تجارية',
      },
    },
    stops: {
      title: 'مفاتيح إيقاف الذكاء الاصطناعي',
      none: 'الذكاء الاصطناعي غير متوقف في أي مكان.',
      loading: 'جارٍ تحميل مفاتيح الإيقاف',
      manage: 'إدارة الإيقافات',
    },
    runs: {
      title: 'أحدث عمليات التشغيل',
      description: 'ما فعله الذكاء الاصطناعي مؤخرًا. لا يُحتفظ هنا بنص أي رسالة.',
      loading: 'جارٍ تحميل عمليات التشغيل',
      viewAll: 'عرض كل عمليات التشغيل',
      empty: {
        title: 'لا توجد عمليات تشغيل بعد',
        description: 'تظهر عمليات التشغيل عندما يجيب الذكاء الاصطناعي أو يكتب مسودة في محادثة.',
      },
    },
  },
  status: {
    connection: {
      active: 'متصل',
      provisioning: 'قيد الإعداد',
      degraded: 'يحتاج إلى متابعة',
      disabled: 'معطّل',
      none: 'غير متصل',
    },
    agent: {
      live: 'منشور · الإصدار {{version}}',
      draftOnly: 'مسودة فقط',
      draftAhead: 'المسودة {{version}} غير منشورة',
      archived: 'مؤرشف',
      none: 'لا يوجد وكيل',
    },
  },
  brands: {
    title: 'العلامات التجارية والتثبيت',
    description: 'أنشئ علامة تجارية، واربطها بـ Chatwoot، وأضف المحادثة إلى موقعك.',
    loading: 'جارٍ تحميل العلامات التجارية',
    create: 'إنشاء علامة تجارية',
    createTitle: 'أنشئ علامة تجارية',
    name: 'اسم العلامة التجارية',
    nameHint: 'يراه العملاء في المحادثة.',
    nameRequired: 'أدخل اسم العلامة التجارية.',
    websites: 'المواقع المسموح بها',
    websitesHint:
      'موقع في كل سطر، مثل https://shop.example.com. هذه المواقع وحدها يمكنها تحميل المحادثة.',
    websiteInvalid: '{{value}} ليس عنوان موقع. استخدم الصيغة https://shop.example.com من دون مسار.',
    area: 'قسم مساحة العمل',
    submit: 'إنشاء العلامة التجارية',
    created: 'أُنشئت {{name}}.',
    widgetId: 'معرّف الأداة',
    allowed: 'المواقع المسموح بها',
    noWebsites: 'لا توجد مواقع مسموح بها بعد، لذا لن تُحمَّل المحادثة في أي مكان.',
    edit: 'تعديل',
    editLabel: 'تعديل {{name}}',
    editTitle: 'تعديل {{name}}',
    save: 'حفظ التغييرات',
    saved: 'تم حفظ {{name}}.',
    removedWarning:
      'عند الحفظ تنتهي المحادثات المفتوحة على المواقع التي أزلتها من القائمة، ولن تظهر الدردشة فيها بعد ذلك:',
    empty: {
      title: 'لا توجد علامات تجارية بعد',
      description: 'لكل علامة تجارية أداة محادثة خاصة بها ومواقع مسموح بها ومساعد ذكاء اصطناعي.',
    },
    chat: {
      title: 'اتصال المحادثة',
      account: 'حساب Chatwoot رقم {{id}}',
      connect: 'اربط Chatwoot',
      connectTitle: 'اربط {{name}} بـ Chatwoot',
      engineNote:
        'يستخدم Hamnivo منصة Chatwoot كمحرك للمحادثات. أنت وفريقك تعملون في Hamnivo Studio، ولا تحتاجون إلى فتح Chatwoot.',
      connectIntro: 'يُعدّ Hamnivo حساب Chatwoot لهذه العلامة التجارية يتضمن:',
      connectItem1: 'مستخدم تكامل وصندوق وارد لمحادثة الموقع،',
      connectItem2: 'روبوت ذكاء اصطناعي يجيب في صندوق الوارد هذا،',
      connectItem3: 'خطافات ويب موقّعة تعود إلى Hamnivo.',
      connectNote:
        'يُنشأ مساعد الذكاء الاصطناعي للعلامة التجارية كمسودة، ولا يجيب العملاء حتى تنشره.',
      connectSubmit: 'ربط',
      connected: 'رُبطت {{name}} بـ Chatwoot.',
      noPermission: 'تحتاج إلى صلاحية إدارة التكاملات لربط Chatwoot.',
      statusHidden: 'لا يرى هذه الحالة إلا من يدير التكاملات لمساحة العمل كلها.',
    },
    install: {
      title: 'ثبّت المحادثة',
      description: 'أضف هذه الشيفرة إلى كل صفحة في موقع مسموح به، قبل وسم </body> الختامي مباشرة.',
      copy: 'انسخ شيفرة التثبيت',
      copied: 'نُسخت شيفرة التثبيت.',
      copyFailed: 'لم ينجح النسخ. حدّد الشيفرة وانسخها بنفسك.',
      note: 'نسخ الشيفرة لا يربط أي شيء بحد ذاته. تظهر المحادثة بعد تشغيل الشيفرة على موقع مسموح به.',
    },
  },
  agents: {
    title: 'وكلاء الذكاء الاصطناعي',
    description:
      'يجيب كل وكيل لعلامة تجارية واحدة. تُحفظ التغييرات كمسودات ولا تصبح منشورة إلا عند النشر.',
    loading: 'جارٍ تحميل الوكلاء',
    columns: {
      answers: 'أين يجيب',
      name: 'الوكيل',
      brand: 'العلامة التجارية',
      model: 'النموذج',
      live: 'الإصدار المنشور',
    },
    isDefault: 'الافتراضي للعلامة التجارية',
    draft: 'الإصدار {{version}}',
    notPublished: 'غير منشور',
    open: 'افتح {{name}}',
    create: 'إنشاء وكيل',
    empty: {
      title: 'لا يوجد وكلاء ذكاء اصطناعي بعد',
      description: 'ربط علامة تجارية بـ Chatwoot يُنشئ مساعدها كمسودة. يمكنك أيضًا إنشاء وكيل هنا.',
    },
    draftModel: 'المسودة: {{model}}',
    noPurpose: 'لم يُكتب غرض بعد.',
    answers: {
      customers:
        'يجيب عملاء {{brand}} في محادثة الموقع وعلى قنوات العلامة التي يعمل فيها الذكاء الاصطناعي.',
      assist: 'يقترح ردودًا على فريقك في محادثات {{brand}}. لا يكتب للعملاء أبدًا.',
      notDefault:
        'لا يجيب أحدًا: يجيب فقط الوكيل الافتراضي لـ {{brand}}. اجعله الافتراضي لاستخدامه.',
      notLive: 'لا يجيب أحدًا بعد: لم يُنشر.',
      archived: 'مؤرشف. لا يجيب أحدًا.',
    },
    filters: {
      search: 'ابحث في الوكلاء',
      searchHint: 'بالاسم أو الاسم المعروض أو الغرض أو العلامة أو النموذج.',
      brand: 'العلامة التجارية',
      allBrands: 'كل العلامات',
      state: 'عرض',
      all: 'كل الوكلاء',
      answering: 'يجيب الآن',
      idle: 'لا يجيب',
      count: 'يُعرض {{shown}} من {{total}} وكيل.',
      none: 'لا يوجد وكيل يطابق عوامل التصفية هذه.',
      clear: 'امسح عوامل التصفية',
    },
    createForm: {
      title: 'أنشئ وكيلًا',
      brand: 'العلامة التجارية',
      name: 'الاسم الداخلي',
      nameHint: 'لا يرى هذا الاسم إلا الموظفون.',
      nameRequired: 'أدخل اسمًا داخليًا.',
      brandRequired: 'اختر علامة تجارية.',
      makeDefault: 'اجعله الوكيل الافتراضي للعلامة التجارية',
      makeDefaultHint: 'يجيب الوكيل الافتراضي عن محادثات العلامة التجارية.',
      submit: 'إنشاء الوكيل',
      created: 'أُنشئ {{name}} كمسودة.',
      noBrands: 'أنشئ علامة تجارية أولًا.',
    },
  },
  editor: {
    loading: 'جارٍ تحميل الوكيل',
    allAgents: 'كل الوكلاء',
    brand: 'العلامة التجارية: {{name}}',
    notLive: {
      title: 'المحفوظ ليس منشورًا',
      live: 'يحصل العملاء على الإصدار {{version}} حتى تنشر.',
      none: 'لا يقدّم هذا الوكيل أي إجابات للعملاء حتى تنشر إصدارًا.',
      draftAhead: 'في مسودة الإصدار {{version}} تغييرات لم تُنشر بعد.',
    },
    unsaved: 'تغييرات غير محفوظة',
    allSaved: 'حُفظت كل التغييرات',
    readOnly: 'يمكنك عرض هذا الوكيل لكن لا يمكنك تغييره.',
    sections: {
      identity: 'الهوية',
      behaviour: 'السلوك',
      model: 'النموذج',
      limits: 'الحدود',
      knowledge: 'المعرفة',
      memory: 'ذاكرة العملاء',
    },
    displayName: 'الاسم الذي يراه العملاء',
    displayNameHint:
      'يظهر بجانب كل رسالة من الذكاء الاصطناعي. صِف الدور، مثل «مساعد المتجر». لا تستخدم اسم شخص.',
    purpose: 'الغرض',
    purposeHint: 'الغرض من هذا الوكيل، في جملة أو جملتين.',
    instructions: 'التعليمات',
    instructionsHint: 'قواعد عملك وأسلوبك. يضيف Hamnivo قواعد للصدق والسلامة لا يمكن إزالتها.',
    tone: 'النبرة',
    tones: {
      warm: 'ودودة',
      warmHint: 'لطيفة وحوارية.',
      neutral: 'محايدة',
      neutralHint: 'بسيطة ومباشرة.',
      formal: 'رسمية',
      formalHint: 'مهذبة ودقيقة.',
    },
    languages: 'لغات الرد',
    languagesHint: 'يُعرض على العميل الذي يكتب بلغة أخرى التحدث إلى شخص.',
    otherLanguages: 'وسوم لغات أخرى',
    otherLanguagesHint: 'اختياري. افصل بينها بفواصل، مثل hi-Latn, ur-Latn.',
    mode: 'الوضع',
    modes: {
      answer: 'الرد على العملاء',
      answerHint: 'يرد في المحادثة.',
      assist: 'مساعدة الموظفين',
      assistHint:
        'يكتب مسودة رد ليوافق عليها فريقك أو يعدّلها أو يرفضها. لا يرد على العملاء من تلقاء نفسه أبدًا.',
    },
    provider: 'المزوّد',
    providers: {
      anthropic: 'Anthropic',
      openai: 'OpenAI',
      google: 'Google Gemini',
      'openai-compatible': 'خادم متوافق مع OpenAI',
      mock: 'نموذج Hamnivo التجريبي (دون اتصال)',
    },
    modelId: 'معرّف النموذج',
    modelIdHint: 'تمامًا كما يسمّيه المزوّد.',
    credentials: 'بيانات الاعتماد',
    platformKey: 'مفتاح الخادم (يُضبط عند تشغيل Hamnivo)',
    credentialsHint: 'استخدم اتصال نموذج محفوظًا، أو المفتاح الذي شُغّل به الخادم.',
    connectionsHidden: 'لا يمكنك رؤية اتصالات النماذج، لذا يبقى الاختيار الحالي كما هو.',
    unknownConnection: 'اتصال محفوظ {{id}}',
    fallback: 'نماذج احتياطية',
    fallbackHint: 'تُجرَّب بالترتيب عندما يكون النموذج الرئيسي مثقلًا أو غير متاح. حتى نموذجين.',
    fallbackItem: 'النموذج الاحتياطي {{n}}',
    addFallback: 'أضف نموذجًا احتياطيًا',
    removeFallback: 'أزل النموذج الاحتياطي {{n}}',
    limits: {
      hint: 'عندما يبلغ التشغيل حدًا ما، يتوقف ويسلّم المحادثة إلى شخص.',
      maxOutputTokens: 'أطول إجابة (بالرموز)',
      timeout: 'المهلة (بالثواني)',
      historyMessages: 'عدد رسائل السجل المقروءة',
      maxSteps: 'أقصى عدد من الخطوات في تشغيل واحد',
    },
    knowledge: {
      hint: 'إجابات العملاء تستخدم المصادر العامة فقط.',
      none: 'لا توجد مصادر معرفة لهذه العلامة التجارية بعد.',
      manage: 'إدارة المعرفة',
      hidden: 'لا يمكنك رؤية مصادر المعرفة.',
    },
    memory: {
      enabled: 'تذكّر تفضيلات العملاء',
      hint: 'فقط للعملاء الذين يوافقون في المحادثة. يمكنهم رؤية ما نتذكره وتصحيحه وحذفه وإيقاف الذاكرة.',
      maxItems: 'أقصى عدد من الذكريات في إجابة واحدة',
      maxChars: 'أقصى عدد من أحرف الذاكرة في إجابة واحدة',
    },
    save: 'حفظ المسودة',
    saved: 'حُفظت المسودة بوصفها الإصدار {{version}}.',
    discard: 'تجاهل التغييرات',
    publish: 'انشر الإصدار {{version}}',
    publishNone: 'نشر',
    publishWhy: {
      unsaved: 'احفظ المسودة قبل النشر.',
      noDraft: 'لا توجد مسودة جديدة للنشر.',
      noPermission: 'تحتاج إلى صلاحية نشر الوكلاء.',
      archived: 'لا يمكن نشر الوكلاء المؤرشفين.',
    },
    publishConfirm: {
      title: 'نشر الإصدار {{version}}؟',
      body: 'يحصل العملاء على إجابات من الإصدار {{version}} فورًا.',
      replaces: 'يتوقف الإصدار {{version}} عن الإجابة.',
      first: 'هذا أول إصدار سيراه العملاء.',
      submit: 'نشر',
    },
    published: 'الإصدار {{version}} منشور الآن.',
    publishFailed: 'لم يُنشر الإصدار {{version}}. لم يتغير الإصدار المنشور.',
    conflict: {
      title: 'حفظ شخص ما مسودة أحدث',
      body: 'حُفظ الإصدار {{version}} أثناء تعديلك. تغييراتك ما زالت هنا.',
      differences: 'الحقول التي تختلف عن المسودة الأحدث:',
      same: 'المسودة الأحدث فيها القيم نفسها التي لديك.',
      loadTheirs: 'حمّل المسودة الأحدث',
      keepMine: 'احتفظ بتغييراتي',
      keepMineHint: 'يحفظ نسختك فوق المسودة الأحدث.',
    },
    versions: {
      title: 'الإصدارات',
      loading: 'جارٍ تحميل الإصدارات',
      state: { draft: 'مسودة', published: 'منشور', retired: 'سابق' },
      version: 'الإصدار {{version}}',
      hash: 'البصمة',
      noHash: 'تُحدَّد عند النشر',
      created: 'أُنشئ {{time}}',
      publishedAt: 'نُشر {{time}}',
      rollback: 'العودة إلى الإصدار {{version}}',
      rollbackTitle: 'العودة إلى الإصدار {{version}}؟',
      rollbackBody: 'يصبح الإصدار {{version}} منشورًا مرة أخرى. تبقى مسودتك ويمكن نشرها لاحقًا.',
      rollbackSubmit: 'العودة',
      rolledBack: 'الإصدار {{version}} منشور مرة أخرى.',
      empty: 'لا توجد إصدارات بعد.',
    },
    leave: {
      title: 'المغادرة دون حفظ؟',
      description: 'تغييراتك على هذا الوكيل غير محفوظة.',
      save: 'حفظ المسودة',
      discard: 'تجاهل',
      stay: 'البقاء',
    },
    errors: {
      summary: 'صحّح هذه الحقول للحفظ',
      displayName: 'أدخل الاسم الذي يراه العملاء، حتى 60 حرفًا.',
      purpose: 'اجعل الغرض أقل من 2000 حرف.',
      instructions: 'اجعل التعليمات أقل من 20000 حرف.',
      languages: 'اختر لغة واحدة على الأقل، وما لا يزيد على 20.',
      languageTag: '{{tag}} ليس وسم لغة. استخدم صيغة مثل hi-Latn.',
      modelId: 'أدخل معرّف النموذج.',
      number: 'أدخل عددًا صحيحًا من {{min}} إلى {{max}}.',
      knowledge: 'اختر 20 مصدرًا على الأكثر.',
    },
  },
  models: {
    title: 'اتصالات النماذج',
    description: 'مفاتيح API لمزوّدي النماذج. تُخزَّن المفاتيح مشفّرة ولا تُعرض مرة أخرى بعد حفظها.',
    loading: 'جارٍ تحميل اتصالات النماذج',
    add: 'إضافة اتصال',
    addTitle: 'أضف اتصال نموذج',
    provider: 'المزوّد',
    label: 'التسمية',
    labelHint: 'لك ولفريقك، مثل «OpenAI للإنتاج».',
    baseUrl: 'عنوان URL الأساسي',
    baseUrlHint: 'مطلوب للخوادم المتوافقة مع OpenAI. اختياري لغيرها (وكيل أو منطقة).',
    apiKey: 'مفتاح API',
    apiKeyHint: 'يُخزَّن مشفّرًا. لن تراه مرة أخرى، بل آخر أحرفه فقط.',
    allowedModels: 'النماذج المسموح بها',
    allowedModelsHint: 'اختياري. معرّف نموذج في كل سطر. اتركه فارغًا للسماح بأي نموذج.',
    submit: 'حفظ الاتصال',
    added: 'حُفظ الاتصال. خُزّن المفتاح مشفّرًا ولن يُعرض مرة أخرى. ينتهي بـ {{hint}}.',
    columns: {
      label: 'التسمية',
      provider: 'المزوّد',
      key: 'المفتاح',
      models: 'النماذج المسموح بها',
      status: 'الحالة',
      created: 'أُضيف',
      actions: 'الإجراءات',
    },
    anyModel: 'أي نموذج',
    keyEnding: 'ينتهي بـ {{hint}}',
    status: { active: 'نشط', disabled: 'معطّل' },
    disable: 'تعطيل',
    disableLabel: 'عطّل {{label}}',
    disableTitle: 'تعطيل {{label}}؟',
    disableBody:
      'يتوقف الوكلاء الذين يستخدمونه عن الإجابة حتى تختار بيانات اعتماد أخرى. يُلغى المفتاح المخزَّن ولا يمكن استرجاعه.',
    disableSubmit: 'تعطيل الاتصال',
    disabled: 'عُطّل {{label}}.',
    empty: {
      title: 'لا توجد اتصالات نماذج بعد',
      description:
        'من دونها يمكن للوكلاء استخدام المزوّدين الذين لدى الخادم مفاتيح لهم فقط، والنموذج التجريبي دون اتصال.',
    },
    errors: {
      label: 'أدخل تسمية.',
      apiKey: 'أدخل مفتاح API. يتكون من 8 أحرف على الأقل.',
      baseUrl: 'أدخل عنوانًا يبدأ بـ http:// أو https://.',
      baseUrlRequired: 'تحتاج الخوادم المتوافقة مع OpenAI إلى عنوان URL أساسي.',
    },
  },
  knowledge: {
    title: 'المعرفة',
    description:
      'المصادر التي يجيب منها الذكاء الاصطناعي. تُفحص المستندات وتُفهرس قبل أن تستخدمها الإجابات.',
    sources: {
      title: 'المصادر',
      loading: 'جارٍ تحميل المصادر',
      add: 'إضافة مصدر',
      documents: 'المستندات: {{total}}',
      review: 'المراجعة كل {{days}} يومًا',
      noReview: 'لا توجد دورة مراجعة',
      settings: 'إعدادات {{name}}',
      empty: {
        title: 'أضف أول مصدر موثوق',
        description: 'زوّد الذكاء الاصطناعي بمعلومات دقيقة من محتوى لديك بالفعل.',
      },
    },
    visibility: {
      public: 'إجابات عامة',
      internal: 'للفريق فقط',
      publicHint: 'يمكن أن تستند إليه الإجابات المقدمة للعملاء.',
      internalHint:
        'لفريقك فقط، كما في البحث في المعرفة. لا يُستخدم أبدًا في الإجابات أو المسودات للعملاء.',
    },
    sourceStatus: { active: 'نشط', paused: 'متوقف مؤقتًا', retired: 'مُستبعد' },
    sourceForm: {
      addTitle: 'أضف مصدر معرفة',
      editTitle: 'إعدادات المصدر',
      brand: 'العلامة التجارية',
      name: 'اسم المصدر',
      nameRequired: 'أدخل اسم المصدر.',
      visibility: 'من يمكنه رؤية الإجابات المستندة إليه',
      review: 'دورة المراجعة بالأيام',
      reviewHint:
        'اختياري. المستندات التي لا تُراجع خلال هذا العدد من الأيام تتوقف عن دعم الإجابات حتى يراجعها أحد.',
      reviewInvalid: 'أدخل عددًا صحيحًا من الأيام من 1 إلى 3650، أو اتركه فارغًا.',
      status: 'الحالة',
      submit: 'إضافة المصدر',
      save: 'حفظ الإعدادات',
      created: 'أُضيف {{name}}.',
      saved: 'حُفظت الإعدادات.',
    },
    documents: {
      title: 'المستندات',
      loading: 'جارٍ تحميل المستندات',
      add: 'إضافة مستند',
      pick: 'اختر مصدرًا لرؤية مستنداته.',
      polling: 'يتحقق Studio من التحديثات كل بضع ثوانٍ أثناء معالجة المستندات.',
      columns: {
        title: 'المستند',
        state: 'الحالة',
        freshness: 'الحداثة',
        version: 'الإصدار',
      },
      select: 'اعرض تفاصيل {{title}}',
      empty: {
        title: 'لا توجد مستندات في هذا المصدر بعد',
        description: 'الصق نصًا أو ارفع ملفًا. يُفحص ويُفهرس قبل أن تستخدمه الإجابات.',
      },
    },
    state: {
      queued: 'في الانتظار',
      processing: 'قيد المعالجة',
      ready: 'جاهز',
      failed: 'فشل',
      rejected: 'مرفوض',
      superseded: 'مُستبدل',
      retired: 'مُستبعد',
    },
    freshness: { current: 'حديث', stale: 'يحتاج إلى مراجعة', expired: 'منتهي الصلاحية' },
    detail: {
      title: 'تفاصيل المستند',
      back: 'العودة إلى المستندات',
      answering: 'الإصدار المستخدم في الإجابات',
      newest: 'أحدث إصدار',
      noneAnswering: 'لا يوجد إصدار يُستخدم في الإجابات بعد.',
      version: 'الإصدار {{version}}',
      size: 'الحجم',
      passages: 'المقاطع',
      added: 'أُضيف',
      readySince: 'جاهز منذ',
      validUntil: 'يُستخدم حتى',
      reviewed: 'آخر مراجعة',
      neverReviewed: 'لم يُراجع بعد',
      reason: 'السبب: {{reason}}',
      original: 'افتح الأصل',
      keepsAnswering: 'يستمر الإصدار {{version}} في الإجابة حتى يجهز الإصدار الجديد.',
      markReviewed: 'تعليم «تمت المراجعة»',
      markedReviewed: 'عُلِّم {{title}} بأنه تمت مراجعته.',
      retire: 'استبعاد',
      retireTitle: 'استبعاد {{title}}؟',
      retireBody: 'تتوقف الإجابات عن استخدام هذا المستند. يبقى في القائمة بوصفه مُستبعدًا.',
      retireSubmit: 'استبعاد المستند',
      retired: 'اُستبعد {{title}}.',
      newVersion: 'ارفع إصدارًا جديدًا',
    },
    upload: {
      addTitle: 'أضف مستندًا',
      versionTitle: 'إصدار جديد من {{title}}',
      docTitle: 'العنوان',
      titleRequired: 'أدخل عنوانًا.',
      url: 'عنوان الأصل (URL)',
      urlHint: 'اختياري. يظهر مع الاستشهادات ليتمكن الناس من قراءة الأصل.',
      urlInvalid: 'أدخل عنوانًا يبدأ بـ http:// أو https://.',
      language: 'وسم اللغة',
      languageHint: 'اختياري، مثل en أو ar أو hi-Latn.',
      languageInvalid: 'استخدم وسم لغة مثل en أو hi-Latn.',
      content: 'المحتوى',
      paste: 'لصق نص',
      file: 'رفع ملف',
      format: 'الصيغة',
      formats: { markdown: 'Markdown', plain: 'نص عادي', html: 'HTML' },
      text: 'النص',
      textRequired: 'الصق النص.',
      fileLabel: 'الملف',
      fileHint: 'PDF أو Word (.docx) أو HTML أو Markdown أو نص عادي، حتى 5 ميغابايت.',
      fileRequired: 'اختر ملفًا.',
      fileTooLarge: 'حجم هذا الملف {{size}}. يمكن أن يصل حجم الملف إلى 5 ميغابايت.',
      fileType:
        'هذا النوع من الملفات غير مدعوم. استخدم PDF أو Word (.docx) أو HTML أو Markdown أو نصًا عاديًا.',
      fileChosen: 'المحدد: {{name}}، {{size}}',
      validUntil: 'التوقف عن استخدامه بعد',
      validUntilHint: 'اختياري، مثلًا لسياسة موسمية.',
      submit: 'إضافة المستند',
      submitVersion: 'رفع الإصدار',
      queued: '{{title}} في الانتظار للفحص.',
    },
    search: {
      title: 'معاينة البحث',
      description: 'اعرف المقاطع التي ستستخدمها الإجابة.',
      brand: 'العلامة التجارية',
      audience: 'الإجابة لـ',
      audiences: {
        customer: 'العملاء (المصادر العامة فقط)',
        staff: 'الموظفين (كل المصادر)',
      },
      query: 'السؤال',
      queryRequired: 'أدخل سؤالًا.',
      submit: 'بحث',
      results: 'المقاطع، الأفضل أولًا',
      found: 'المقاطع التي وُجدت: {{total}}',
      score: 'الدرجة {{score}}',
      version: 'الإصدار {{version}}',
      empty: 'لا توجد مقاطع مطابقة. ستطلب الإجابة مزيدًا من التفاصيل أو تعرض التحدث إلى شخص.',
      noBrands: 'أنشئ علامة تجارية للبحث في معرفتها.',
    },
  },
  stopAi: {
    title: 'إيقاف الذكاء الاصطناعي',
    description:
      'أوقف ردود الذكاء الاصطناعي فورًا. يستمر الأشخاص في المحادثة، وتذهب الرسائل الجديدة إلى فريقك.',
    loading: 'جارٍ تحميل مفاتيح الإيقاف',
    dialogTitle: 'إيقاف ردود الذكاء الاصطناعي',
    dialogDescription: 'يتوقف الذكاء الاصطناعي عن الإجابة حيث تختار. تبقى المحادثات مفتوحة لفريقك.',
    scope: 'أين يتوقف',
    scopes: {
      tenant: 'في كل مكان في مساحة العمل هذه',
      brand: 'علامة تجارية واحدة',
      agent: 'وكيل واحد',
    },
    brand: 'العلامة التجارية',
    agent: 'الوكيل',
    choose: 'اختر…',
    reason: 'السبب',
    reasonHint: 'يرى فريقك هذا. مثلًا: «أسعار خاطئة في الإجابات».',
    submit: 'إيقاف ردود الذكاء الاصطناعي',
    stopped: 'أُوقفت ردود الذكاء الاصطناعي. {{scope}}.',
    errors: {
      reason: 'اذكر سببًا من 3 أحرف على الأقل.',
      target: 'اختر أين يتوقف الذكاء الاصطناعي.',
    },
    active: { title: 'الإيقافات النشطة', empty: 'الذكاء الاصطناعي غير متوقف في أي مكان.' },
    past: { title: 'الإيقافات السابقة', empty: 'لا توجد إيقافات سابقة.' },
    columns: {
      since: 'أُوقف',
      by: 'بواسطة',
      cleared: 'استُؤنف',
    },
    status: { active: 'الذكاء الاصطناعي متوقف', cleared: 'استُؤنف' },
    clear: 'اسمح للذكاء الاصطناعي بالإجابة مجددًا',
    clearLabel: 'اسمح للذكاء الاصطناعي بالإجابة مجددًا: {{scope}}',
    clearTitle: 'السماح للذكاء الاصطناعي بالإجابة مجددًا؟',
    clearBody: 'تُستأنف ردود الذكاء الاصطناعي في: {{scope}}. تأكد من معالجة سبب الإيقاف.',
    clearSubmit: 'اسمح بالإجابة',
    cleared: 'استُؤنفت ردود الذكاء الاصطناعي. {{scope}}.',
    noPermission: 'تحتاج إلى صلاحية نشر الوكلاء لإيقاف الذكاء الاصطناعي أو استئنافه.',
    you: 'أنت',
    teammate: 'أحد الزملاء',
    unknownTarget: 'غير معروف ({{id}})',
  },
  runs: {
    title: 'عمليات التشغيل',
    description:
      'ما فعلته كل عملية تشغيل للذكاء الاصطناعي: النتيجة والإصدار والنموذج والرموز والوقت. لا يُحتفظ هنا بالتعليمات أو الردود.',
    loading: 'جارٍ تحميل عمليات التشغيل',
    filters: {
      label: 'تصفية عمليات التشغيل',
      agent: 'الوكيل',
      allAgents: 'كل الوكلاء',
      outcome: 'النتيجة',
      allOutcomes: 'كل النتائج',
      conversation: 'معرّف المحادثة',
      conversationHint: 'اختياري، يبدأ بـ conv_',
      apply: 'تطبيق التصفية',
      reset: 'إعادة تعيين التصفية',
      outcomeNote: 'تنطبق تصفية النتيجة على عمليات التشغيل المحمّلة في هذه الصفحة.',
    },
    columns: {
      started: 'البدء',
      outcome: 'النتيجة',
      agent: 'الوكيل والإصدار',
      model: 'النموذج',
      tokens: 'الرموز الداخلة / الخارجة',
      latency: 'المدة',
      details: 'التفاصيل',
    },
    outcome: {
      replied: 'ردّ',
      drafted: 'مسودة للموظفين',
      handoff: 'سُلّمت إلى شخص',
      fenced: 'أُهمل لأن المحادثة تقدّمت',
      blocked: 'منعه حارس',
      error: 'خطأ',
      skipped: 'تُخطّي',
      cancelled: 'أوقفه شخص',
      running: 'قيد التشغيل',
    },
    view: 'عرض',
    viewLabel: 'اعرض التشغيل الذي بدأ {{time}}',
    loadMore: 'حمّل مزيدًا من عمليات التشغيل',
    shown: 'عمليات التشغيل المعروضة: {{total}}',
    empty: {
      title: 'لا توجد عمليات تشغيل بعد',
      description: 'تظهر عمليات التشغيل عندما يجيب الذكاء الاصطناعي أو يكتب مسودة في محادثة.',
      action: 'افتح وكلاء الذكاء الاصطناعي',
    },
    noMatches: {
      title: 'لا توجد عمليات تشغيل تطابق هذه التصفية',
      description: 'أزل أحد عوامل التصفية لترى مزيدًا من عمليات التشغيل.',
    },
    detail: {
      title: 'تفاصيل التشغيل',
      loading: 'جارٍ تحميل التشغيل',
      summary: 'الملخص',
      states: {
        running: 'قيد التشغيل',
        succeeded: 'نجح',
        failed: 'فشل',
        skipped: 'تُخطّي',
      },
      kind: 'النوع',
      kinds: { reply: 'رد على العميل', draft: 'مسودة للموظفين', guard: 'حارس الرسائل' },
      conversation: 'المحادثة',
      started: 'البدء',
      finished: 'الانتهاء',
      errorKind: 'نوع الخطأ',
      steps: 'الخطوات',
      noSteps: 'لم تُسجَّل أي خطوات.',
      step: 'الخطوة {{n}}',
      stepKinds: {
        model: 'النموذج',
        guard: 'الحارس',
        retrieval: 'بحث في المعرفة',
        decision: 'القرار',
        tool: 'أداة',
      },
      stepStatus: { ok: 'تم', error: 'خطأ', blocked: 'ممنوع', skipped: 'تُخطّي' },
      noDetail: 'لا توجد تفاصيل أخرى.',
      version: 'الإصدار {{version}}',
      noAgent: 'لا يوجد وكيل',
    },
  },
  inbox: {
    title: 'صندوق الوارد',
    documentTitle: '{{name}} · صندوق الوارد',
    loading: 'جارٍ تحميل المحادثات',
    conversationLoading: 'جارٍ تحميل المحادثة',
    loadMore: 'عرض المزيد من المحادثات',
    shown: 'المحادثات المعروضة: {{n}}',
    unavailable: 'هذه المحادثة غير متاحة',
    notFound: 'قد تكون في مساحة عمل أو علامة تجارية لا يمكنك رؤيتها، أو أن الرابط خاطئ.',
    back: 'العودة إلى صندوق الوارد',
    forbidden: {
      title: 'لا يمكنك رؤية المحادثات هنا',
      description: 'اطلب من مالك مساحة العمل أو المسؤول دورًا يشمل صندوق الوارد.',
    },
    queues: {
      label: 'قوائم الانتظار',
      mine: 'محادثاتي',
      unassigned: 'بانتظار شخص',
      overdue: 'متأخرة',
      ai: 'يجيب الذكاء الاصطناعي',
      team: 'فرقي',
      tickets: 'التذاكر',
      all: 'الكل',
    },
    tickets: ticketsAr,
    sla: slaAr,
    aiDrafts: aiDraftsAr,
    offers: offersAr,
    blocking: blockingAr,
    keys: {
      plain: 'المفاتيح: ↑ ↓ أو J K للتنقل، و Enter للفتح، و Esc للرجوع.',
      withSelect: 'المفاتيح: ↑ ↓ أو J K للتنقل، و Enter للفتح، و X للتحديد، و Esc للرجوع.',
    },
    search: {
      label: 'البحث في المحادثات والرسائل',
      placeholder: 'ابحث في الأسماء أو البريد الإلكتروني أو نص الرسائل',
      submit: 'بحث',
      loading: 'جارٍ البحث',
      tooShort: 'اكتب حرفين على الأقل.',
      found: 'المحادثات: {{conversations}}. الرسائل: {{messages}}.',
      conversations: 'العملاء',
      messages: 'الرسائل',
      none: 'لم يُعثر على شيء',
      noneHint: 'البحث يشمل المحادثات التي يمكنك رؤيتها فقط.',
      clear: 'مسح البحث',
      more: 'عرض المزيد من الرسائل',
    },
    filters: {
      button: 'عوامل التصفية',
      buttonCount: 'عوامل التصفية ({{n}})',
      title: 'تصفية قائمة الانتظار',
      close: 'إغلاق عوامل التصفية',
      status: 'الحالة',
      statuses: {
        active: 'مفتوحة أو معلّقة',
        snoozed: 'مؤجلة',
        resolved: 'محلولة',
        all: 'أي حالة',
      },
      brand: 'العلامة التجارية',
      allBrands: 'كل العلامات التجارية',
      channel: 'القناة',
      allChannels: 'كل القنوات',
      language: 'لغة العميل',
      allLanguages: 'كل اللغات',
      tag: 'الوسم',
      allTags: 'كل الوسوم',
      active: 'عوامل التصفية المفعّلة: {{n}}',
      clear: 'مسح عوامل التصفية',
    },
    empty: {
      filteredTitle: 'لا توجد محادثات تطابق عوامل التصفية',
      filteredDescription: 'امسح عوامل التصفية لرؤية قائمة الانتظار كاملة.',
      seeAll: 'عرض كل المحادثات',
      overview: 'الانتقال إلى النظرة العامة',
      mine: {
        title: 'لا شيء مُسند إليك',
        description: 'تظهر هنا المحادثات التي تتولاها أو تُسند إليك.',
      },
      unassigned: {
        title: 'لا أحد ينتظر شخصًا',
        description: 'عندما يطلب عميل شخصًا، تنتظر المحادثة هنا.',
      },
      overdue: {
        title: 'لا شيء متأخر',
        description: 'تظهر هنا المحادثات التي تجاوزت هدف وقت الرد، الأكثر تأخرًا أولًا.',
      },
      ai: {
        title: 'الذكاء الاصطناعي لا يجيب أحدًا الآن',
        description: 'تظهر هنا المحادثات التي يجيب عنها الذكاء الاصطناعي.',
      },
      team: {
        title: 'لا شيء في فرقك',
        description: 'تظهر هنا المحادثات المُسندة إلى فرقك.',
      },
      tickets: {
        title: 'لا توجد تذاكر بعد',
        description:
          'تصبح الرسائل المتروكة عبر نموذج التواصل حين لا يتوفر من يرد تذاكر، وكذلك أي محادثة تحوّلها إلى تذكرة.',
      },
      all: {
        title: 'لا توجد محادثات بعد',
        description: 'تظهر المحادثات هنا عندما يكتب العملاء عبر علامة تجارية متصلة.',
      },
    },
    pick: {
      title: 'اختر محادثة',
      description: 'افتح محادثة من قائمة الانتظار لقراءتها والرد عليها.',
    },
    connection: {
      reconnecting:
        'جارٍ إعادة الاتصال… قد تتأخر التحديثات؛ يتحقق صندوق الوارد كل 15 ثانية. مسوداتك محفوظة.',
      offline: 'أنت غير متصل. تبقى مسوداتك في هذا المتصفح حتى تعود.',
    },
    row: {
      noMessages: 'لا توجد رسائل بعد',
      team: 'الفريق: {{name}}',
      from: { ai: 'الذكاء الاصطناعي:', staff: 'الفريق:', system: 'ملاحظة:' },
    },
    owner: {
      ai: 'يجيب الذكاء الاصطناعي',
      waiting: 'بانتظار شخص',
      waitingFor: 'بالانتظار منذ {{time}}',
      paused: 'الذكاء الاصطناعي متوقف مؤقتًا',
      you: 'أنت',
      person: 'شخص',
      external: 'شخص في Chatwoot',
    },
    status: {
      open: 'مفتوحة',
      pending: 'معلّقة',
      snoozed: 'مؤجلة',
      snoozedUntil: 'مؤجلة حتى {{time}}',
      resolved: 'محلولة',
      resolve: 'حلّ',
      resolvedDone: 'حُلّت المحادثة.',
      reopen: 'إعادة فتح',
      wake: 'إلغاء التأجيل',
      reopened: 'أُعيد فتح المحادثة. أنت تتولاها الآن.',
    },
    identity: {
      verified: 'عميل موثَّق',
      anonymous: 'غير مسجّل الدخول',
      unknown: 'عميل غير معروف',
    },
    channel: {
      chat: 'دردشة الموقع',
      email: 'البريد الإلكتروني',
      whatsapp: 'WhatsApp',
      telegram: 'Telegram',
      sms: 'رسائل SMS',
      other: 'قناة أخرى',
    },
    details: {
      title: 'التفاصيل',
      show: 'التفاصيل',
      hide: 'إخفاء التفاصيل',
    },
    banner: {
      ai: '{{name}} يجيب. الرد أو تولّي المحادثة يوقف الذكاء الاصطناعي مؤقتًا في هذه المحادثة.',
      other: '{{name}} يتولى هذه المحادثة.',
      otherSince: '{{name}} يتولى هذه المحادثة. تولّاها {{time}}.',
      notesStill: 'لا يزال بإمكانك إضافة ملاحظات داخلية.',
      asked: 'طلب العميل التحدث إلى شخص.',
      askedSince: 'طلب العميل التحدث إلى شخص {{time}}.',
      released: 'أُعيدت هذه المحادثة إلى قائمة الانتظار.',
      channelAiOff: 'الذكاء الاصطناعي لا يرد على هذه القناة، لذا يجب أن يرد شخص من فريقك.',
      waiting: 'هذه المحادثة بانتظار شخص.',
      takeOver: 'تولَّ المحادثة',
      claim: 'أسندها إليّ',
      claimLost: 'تولّى شخص آخر هذه المحادثة أولًا. العرض يُظهر الآن من يتولاها.',
    },
    presence: {
      viewing: '{{name}} يشاهد',
      typingReply: '{{name}} يكتب ردًا',
      typingNote: '{{name}} يكتب ملاحظة',
      alsoReply: '{{name}} يكتب ردًا هنا أيضًا.',
      alsoNote: '{{name}} يكتب ملاحظة هنا أيضًا.',
    },
    assign: {
      button: 'إسناد',
      me: 'أسندها إليّ',
      colleagues: 'زملاء يمكنهم الرد',
      current: 'يتولاها الآن',
      queue: 'إعادة إلى قائمة الانتظار',
      ai: 'إعادة إلى الذكاء الاصطناعي',
      team: 'الفريق',
      teamCurrent: 'الفريق: {{name}}',
      noTeams: 'لا توجد فرق بعد',
      noTeam: 'بلا فريق',
      doneMe: 'أنت تتولى هذه المحادثة الآن.',
      doneTo: 'أُسندت إلى {{name}}.',
      doneQueue: 'أُعيدت إلى قائمة الانتظار.',
      doneAi: 'الذكاء الاصطناعي يجيب مرة أخرى.',
      doneTeam: 'أُسندت إلى فريق {{name}}.',
      doneNoTeam: 'أُخرجت من فريقها.',
    },
    transfer: {
      ask: 'اطلب تولّي المحادثة',
      title: 'اطلب تولّي المحادثة',
      description:
        'تصل إلى {{name}} ملاحظة داخلية تطلب منه تسليم هذه المحادثة إليك. لا يتغير شيء حتى يفعل ذلك هو أو أحد المشرفين.',
      note: 'السبب (لزميلك)',
      send: 'إرسال الطلب',
      sent: 'أُرسل الطلب إلى {{name}}.',
    },
    snooze: {
      button: 'تأجيل',
      hour: 'لمدة ساعة',
      tomorrow: 'حتى الغد ({{time}})',
      nextWeek: 'حتى الأسبوع القادم ({{time}})',
      pick: 'اختر وقتًا…',
      pickTitle: 'تأجيل حتى',
      pickDescription: 'تعود المحادثة إلى قائمة الانتظار في هذا الوقت، أو عندما يكتب العميل.',
      until: 'التاريخ والوقت',
      future: 'اختر وقتًا في المستقبل.',
      confirm: 'تأجيل',
      done: 'أُجّلت حتى {{time}}.',
    },
    timeline: {
      label: 'الرسائل',
      loading: 'جارٍ تحميل الرسائل',
      older: 'عرض الرسائل الأقدم',
      empty: 'لا توجد رسائل بعد.',
    },
    roles: {
      customer: 'عميل',
      staff: 'موظف دعم',
      you: 'أنت',
      ai: 'مساعد ذكاء اصطناعي',
      specialist: 'متخصص ذكاء اصطناعي',
      guest: 'ضيف',
      system: 'النظام',
    },
    lanes: {
      public: 'رد',
      publicDescription: 'يرى العميل الردود.',
      internal: 'ملاحظة داخلية',
      internalDescription: 'يرى فريقك الملاحظات فقط. لا يراها العميل أبدًا.',
    },
    message: {
      handoffSummary: 'ملخص التسليم',
      staffOnly: 'للفريق فقط',
      sources: 'المصادر',
      newFrom: 'رسالة جديدة من {{name}}.',
      file: {
        quarantined: 'بانتظار الفحص',
        scanning: 'قيد الفحص',
        available: 'متاح',
        rejected: 'محظور',
      },
      place: 'شارك مكانًا: {{label}}',
      contact: 'شارك جهة اتصال: {{details}}',
      reaction: 'تفاعل بـ {{emoji}}',
    },
    translation: {
      translate: 'ترجمة إلى {{language}}',
      label: 'ترجمة آلية إلى {{language}}. النص الأصلي في الأعلى.',
      hide: 'إخفاء',
      show: 'إظهار',
      failed: 'لم تنجح الترجمة. الرسالة الأصلية لم تتغير.',
    },
    composer: {
      lanes: 'اكتب ردًا أو ملاحظة',
      replyLabel: 'الرد على {{name}}',
      noteLabel: 'ملاحظة داخلية',
      replyPlaceholder: 'اكتب ردًا إلى {{name}}…',
      notePlaceholder: 'اكتب ملاحظة لفريقك…',
      send: 'إرسال',
      addNote: 'إضافة ملاحظة',
      toSend: 'للإرسال',
      sent: 'أُرسل الرد.',
      noteAdded: 'أُضيفت الملاحظة.',
      notSent: 'لم يُرسل.',
      saving: 'جارٍ حفظ المسودة…',
      saved: 'حُفظت المسودة',
      savedHere: 'المسودة محفوظة في هذا المتصفح',
      offlineSaved: 'غير متصل: المسودة محفوظة في هذا المتصفح',
      hasDraft: '(مسودة)',
      takesOver: 'إرسال رد يُسند هذه المحادثة إليك.',
      takesOverAi: 'إرسال رد يتولى المحادثة بدلًا من الذكاء الاصطناعي.',
      noReply: 'دورك يسمح بقراءة هذه المحادثة لا بالرد عليها.',
      noNote: 'دورك لا يسمح بإضافة ملاحظات هنا.',
      ownedByOther: '{{name}} يتولى هذه المحادثة. اطلب تولّيها قبل الرد.',
      staleSend:
        'لم يُرسل: غيّر شخص آخر من يتولى هذه المحادثة. نصك محفوظ في الأسفل. راجع المحادثة ثم حاول مرة أخرى.',
      conflict: 'تغيّرت هذه المسودة في علامة تبويب أو جهاز آخر.',
      keepMine: 'الاحتفاظ بنسختي',
      useTheirs: 'استخدام النسخة الأخرى',
    },
    outgoing: {
      label: 'رسائل لم تُرسل بعد',
      sending: 'جارٍ الإرسال',
      failed: 'لم تُرسل',
      ownership: 'تولّى شخص آخر المحادثة قبل الإرسال. لم يصل شيء إلى العميل.',
      unavailable: 'لم يستجب Chatwoot. الإرسال مرة أخرى آمن: لا تُرسل الرسالة مرتين أبدًا.',
      other: 'تعذّر الإرسال. حاول مرة أخرى.',
      retry: 'حاول مرة أخرى',
      discard: 'تجاهل',
      discarded: 'تم التجاهل.',
    },
    saved: {
      button: 'الردود المحفوظة',
      search: 'البحث في الردود المحفوظة',
      none: 'لم يُعثر على ردود محفوظة.',
      missing: 'أكمل يدويًا: {{names}}',
    },
    copilot: {
      button: 'اقترح ردًا',
      title: 'رد مقترح',
      review: 'كتبه {{name}} (ذكاء اصطناعي). راجعه قبل الإرسال؛ لا يُرسل شيء من تلقاء نفسه.',
      noSources: 'لم تُستخدم أي مصادر.',
      use: 'تعديل في الرد',
      copy: 'نسخ',
      sendAsMe: 'أرسل باسمي',
      dismiss: 'تجاهل الاقتراح',
      ready: 'الرد المقترح جاهز.',
      off: 'مساعد الذكاء الاصطناعي متوقف لهذه العلامة التجارية، لذا لا يمكنه اقتراح ردود.',
    },
    customer: {
      title: 'العميل',
      verifiedHint: 'سجّل الدخول في موقع العلامة التجارية؛ تم التحقق من هويته.',
      anonymousHint: 'غير مسجّل الدخول. لم يتم التحقق من اسمه وبياناته.',
      email: 'البريد الإلكتروني',
      unverifiedEmail: 'البريد المُدخل',
      notVerified: '(غير موثَّق)',
      externalId: 'معرّف العميل',
      language: 'اللغة',
      brand: 'العلامة التجارية',
      since: 'عميل منذ',
      lastSeen: 'آخر ظهور',
      previous: 'محادثات أخرى',
      noPrevious: 'لا توجد محادثات أخرى يمكنك رؤيتها.',
    },
    tags: {
      title: 'الوسوم',
      none: 'لا توجد وسوم بعد.',
      add: 'إضافة وسم',
      choose: 'اختر وسمًا',
      addButton: 'إضافة',
      remove: 'إزالة الوسم {{name}}',
      added: 'أُضيف الوسم {{name}}.',
      removed: 'أُزيل الوسم {{name}}.',
      noneDefined: 'لم تُعدّ وسوم لهذه العلامة التجارية بعد.',
    },
    fields: {
      title: 'الحقول المخصصة',
      none: 'لا توجد حقول مخصصة لهذه العلامة التجارية.',
      save: 'حفظ الحقول',
      saved: 'حُفظت الحقول.',
    },
    links: {
      title: 'محادثات مرتبطة',
      none: 'لا توجد محادثات مرتبطة.',
      other: 'معرّف المحادثة المراد ربطها',
      otherHint: 'مثل conv_…؛ تبقى المحادثتان منفصلتين.',
      note: 'سبب الربط',
      add: 'ربط',
      added: 'رُبطت المحادثتان.',
      remove: 'إزالة الرابط مع {{name}}',
      removed: 'أُزيل الرابط.',
    },
    reviews: {
      title: 'مراجعة المشرف',
      none: 'لا توجد مراجعات بعد.',
      rating: '{{rating}} من 5',
      ratingLabel: 'التقييم',
      note: 'ملاحظة توجيهية',
      save: 'حفظ المراجعة',
      saved: 'حُفظت المراجعة.',
      by: 'بواسطة {{reviewer}}',
      byAbout: 'بواسطة {{reviewer}}، عن {{subject}}',
    },
    bulk: {
      select: 'تحديد {{name}}',
      selected: 'حُدّد {{name}}. المحدد: {{n}}.',
      unselected: 'أُلغي تحديد {{name}}. المحدد: {{n}}.',
      count: 'المحدد: {{n}}',
      clear: 'مسح التحديد',
      assignMe: 'أسندها إليّ',
      resolve: 'حلّ',
      reopen: 'إعادة فتح',
      tagLabel: 'الوسم المراد إضافته',
      tagPlaceholder: 'الوسم المراد إضافته',
      addTag: 'إضافة وسم',
      confirmTitle: 'تغيير المحادثات المحددة؟',
      willChange: 'يمكن تغييرها: {{n}}.',
      cannotChange: 'لا يمكن تغييرها: {{n}}.',
      apply: 'تغيير {{n}}',
      result: 'تم: {{done}}. فشل: {{failed}}.',
      partial: 'تم: {{done}}. لم يتغير: {{failed}}. يمكن إعادة المحاولة لهذه فقط.',
      retryFailed: 'إعادة محاولة ما فشل',
      failed: 'لم يتغير',
    },
  },
  account: {
    title: 'أمان الحساب',
    description: 'كيف تسجّل الدخول إلى Studio.',
    twoFactor: {
      title: 'التحقق بخطوتين',
      on: 'مفعّل',
      off: 'غير مفعّل',
      description: 'يطلب Studio رمزًا من تطبيق المصادقة عند تسجيل الدخول. بعض الإجراءات تتطلبه.',
      enable: 'تفعيل',
      disable: 'إيقاف',
      password: 'كلمة المرور',
      passwordHint: 'أدخل كلمة المرور لتغيير هذا الإعداد.',
      setupTitle: 'أضف Hamnivo إلى تطبيق المصادقة',
      setupBody: 'أدخل مفتاح الإعداد هذا في تطبيقك، أو افتح الرابط على هذا الجهاز.',
      secret: 'مفتاح الإعداد',
      openApp: 'افتح في تطبيق المصادقة',
      backupTitle: 'احفظ رموزك الاحتياطية',
      backupBody:
        'كل رمز يتيح لك تسجيل الدخول مرة واحدة إذا فقدت هاتفك. احفظها في مكان آمن. لا تُعرض إلا الآن.',
      copyCodes: 'انسخ الرموز الاحتياطية',
      verify: 'تحقّق وفعّل',
      enabled: 'التحقق بخطوتين مفعّل.',
      disabled: 'التحقق بخطوتين غير مفعّل.',
      disableBody:
        'سيطلب تسجيل الدخول كلمة المرور فقط. ستتوقف لديك الإجراءات التي تتطلب التحقق بخطوتين.',
      wrongPassword: 'كلمة المرور هذه غير صحيحة.',
    },
    passkeys: {
      title: 'مفاتيح المرور',
      description: 'سجّل الدخول بقفل شاشة جهازك بدلًا من كلمة المرور.',
      add: 'إضافة مفتاح مرور',
      added: 'أُضيف مفتاح المرور.',
      failed: 'لم يُضف مفتاح المرور. حاول مرة أخرى.',
      unsupported: 'لا يستطيع هذا المتصفح استخدام مفاتيح المرور.',
      none: 'لا توجد مفاتيح مرور بعد.',
      created: 'أُضيف {{time}}',
      unnamed: 'مفتاح مرور',
      loading: 'جارٍ تحميل مفاتيح المرور',
    },
  },
  decisions: decisionsAr,
  checks: checksAr,
  caching: cachingAr,
  tools: toolsAr,
  commerce: commerceAr,
  developer: developerAr,
  reports: reportsAr,
  privacy: privacyAr,
  chatSettings: chatSettingsAr,
  chatWidget: chatWidgetAr,
  channels: channelsAr,
  automations: automationsAr,
  memory: {
    title: 'ذاكرة العملاء',
    description: 'أدِر ما نتذكره عن عملائك.',
    allCustomers: 'كل العملاء',
    loading: 'جارٍ تحميل ذاكرة العميل',
    notOffered:
      'وكيل الذكاء الاصطناعي لهذه العلامة التجارية لا يستخدم الذاكرة الآن، لذلك لا يُستخدم شيء هنا في الردود.',
    exported: 'تم تصدير الذاكرة.',
    forbidden: {
      title: 'لا يمكنك رؤية ذاكرة العملاء',
      description: 'اطلب من مسؤول مساحة العمل صلاحية الوصول إلى ذاكرة العملاء.',
    },
    list: {
      title: 'العملاء',
      search: 'ابحث عن العملاء',
      searchHint: 'الاسم أو البريد الإلكتروني أو معرّف العميل.',
      searchLabel: 'البحث في ذاكرة العملاء',
      submit: 'بحث',
      brand: 'العلامة التجارية',
      allBrands: 'كل العلامات التجارية',
      loading: 'جارٍ تحميل العملاء',
      toReview: 'للمراجعة: {{total}}',
      columns: {
        customer: 'العميل',
        brand: 'العلامة التجارية',
        choice: 'الذاكرة',
        remembered: 'المحفوظ',
        review: 'بحاجة إلى مراجعة',
        changed: 'آخر تغيير',
      },
      empty: {
        title: 'لا توجد ذاكرة عملاء بعد',
        description:
          'يظهر العملاء هنا بعد موافقتهم على التذكّر في المحادثة. شغّل الذاكرة من إعدادات وكيل الذكاء الاصطناعي.',
        action: 'فتح وكلاء الذكاء الاصطناعي',
      },
      noMatch: {
        title: 'لا يوجد عملاء مطابقون',
        description: 'جرّب اسمًا أو بريدًا إلكترونيًا أو معرّفًا آخر.',
      },
    },
    customer: {
      anonymous: 'عميل مجهول',
      unnamed: 'عميل مسجّل الدخول',
      verified: 'مسجّل الدخول',
      notVerified: 'غير مسجّل الدخول',
      since: 'عميل منذ {{date}}',
    },
    consent: {
      granted: 'الذاكرة مفعّلة',
      declined: 'رفض',
      withdrawn: 'الذاكرة متوقفة',
      undecided: 'لم يُسأل بعد',
      history: 'سجل الاختيارات',
      by: {
        customer: 'من العميل',
        staff: 'من الموظفين',
      },
      line: {
        granted: 'وافق في {{date}}',
        declined: 'رفض في {{date}}',
        withdrawn: 'أوقفها في {{date}}',
        undecided: 'لم يختر العميل بعد.',
      },
    },
    status: {
      active: 'حالية',
      pending: 'بحاجة إلى مراجعة',
      superseded: 'مستبدَلة',
      outdated: 'قديمة',
    },
    sources: {
      customer_statement: 'رسالة من العميل',
      customer_control: 'حدّدها العميل',
      staff_entry: 'أدخلها الموظفون',
      agent_inference: 'مستنتجة',
      system_record: 'سجل النظام',
      import: 'مستوردة',
    },
    writers: {
      ai: 'المساعد الذكي',
      staff: 'موظف',
      customer: 'العميل',
      system: 'Hamnivo',
    },
    reasons: {
      extracted_from_message: 'من رسالة العميل',
      customer_correction: 'صحّحها العميل',
      customer_confirmation: 'أكّدها العميل',
      staff_correction: 'صحّحها الموظفون',
      staff_confirmation: 'أكّدها الموظفون',
    },
    scope: {
      short: {
        person: 'هذا العميل',
        conversation: 'هذه المحادثة فقط',
      },
      long: {
        person: 'هذا العميل، في كل محادثة مع هذه العلامة التجارية',
        conversation: 'المحادثة التي جاءت منها فقط',
      },
    },
    slots: {
      preferred_language: {
        label: 'اللغة المفضلة',
        purpose: 'الرد بلغة العميل',
      },
      preferred_name: {
        label: 'الاسم المفضل',
        purpose: 'مخاطبة العميل كما طلب',
      },
      contact_method: {
        label: 'طريقة التواصل المفضلة',
        purpose: 'استخدام طريقة التواصل التي يفضلها',
      },
      contact_time: {
        label: 'أفضل وقت للتواصل',
        purpose: 'التواصل في الوقت المناسب له',
      },
      communication_style: {
        label: 'أسلوب الرد',
        purpose: 'الرد بالطريقة التي يفضلها',
      },
      delivery_note: {
        label: 'ملاحظة التوصيل',
        purpose: 'ضمان توصيل سلس',
      },
      product_interest: {
        label: 'اهتمام بمنتج',
        purpose: 'اقتراح ما سأل عنه',
      },
      size_preference: {
        label: 'المقاس',
        purpose: 'اقتراح المقاس المناسب',
      },
      open_issue: {
        label: 'طلب مفتوح',
        purpose: 'المتابعة دون السؤال مجددًا',
      },
      stated_order_reference: {
        label: 'طلب ذكره العميل',
        purpose: 'العثور على طلبه أسرع',
      },
    },
    tabs: {
      preferences: 'التفضيلات',
      facts: 'حقائق من المحادثات',
      notes: 'ملاحظات الوكيل',
      internal: 'داخلي فقط',
    },
    table: {
      title: 'ما نتذكره',
      columns: {
        memory: 'الذكرى',
        purpose: 'الغرض',
        source: 'المصدر',
        confirmed: 'آخر تأكيد',
        visibility: 'الظهور',
      },
      noMatch: 'لا شيء يطابق بحثك.',
      emptyPreferences: 'لا توجد تفضيلات محفوظة.',
      emptyFacts: 'لا توجد حقائق محفوظة من المحادثات.',
      searchPreferences: 'ابحث في تفضيلات العميل…',
      searchFacts: 'ابحث في حقائق المحادثات…',
      notes:
        'لا يحتفظ المساعد بملاحظات عمل عن العملاء. يتذكر فقط ما يخبره به العملاء بعد موافقتهم.',
    },
    panel: {
      pick: 'اختر ذكرى لترى مصدرها.',
      onlyApproved: 'لا تُستخدم في ردود العملاء إلا القيم المعتمدة.',
      value: 'القيمة الحالية',
      status: 'الحالة',
      purpose: 'الغرض',
      source: 'المصدر',
      messages: 'الرسالة:',
      conversation: 'المحادثة:',
      recordedBy: 'سجّلها',
      note: 'سبب التغيير',
      scope: 'النطاق',
      inEffect: 'سارية منذ',
      expires: 'تنتهي',
      noExpiry: 'بلا تاريخ انتهاء',
      verified: 'التحقق',
      notVerified: 'لم يتم التحقق. هذا ما قاله العميل.',
      version: 'الإصدار',
      edit: 'تعديل القيمة',
      newValue: 'القيمة الجديدة',
      correctionReason: 'سبب التصحيح',
      valueRequired: 'أدخل القيمة الجديدة.',
      valueSame: 'أدخل قيمة مختلفة عن الحالية.',
      saved: 'تم حفظ التصحيح. تبقى القيمة القديمة في السجل.',
      unchanged: 'لم يتغير شيء: هذه القيمة محفوظة بالفعل.',
      history: 'السجل',
      historyLine: '{{writer}} · من {{from}} إلى {{until}}',
    },
    reason: {
      label: 'السبب',
      hint: 'لا تُدرج تفاصيل شخصية. يُحفظ السبب مع التغيير.',
      required: 'أدخل سببًا.',
    },
    actions: {
      export: 'تصدير',
      erase: 'محو كل الذاكرة',
      outdated: 'وضع علامة «قديمة»',
      delete: 'حذف',
      save: 'حفظ التصحيح',
    },
    conflict: {
      title: 'قد تستبدل رسالة أحدث «{{label}}»',
      body: 'هناك رسالة أحدث من {{date}}. راجعها وأكّد أيّ القيمتين تحتفظ بها.',
      bodyNoCurrent: 'قدّمت رسالة من {{date}} قيمة تنتظر المراجعة.',
      customerCan:
        'يمكن للعميل أيضًا تأكيدها في المحادثة. حتى ذلك الحين لا تُستخدم أي من القيمتين في الردود.',
      compare: 'مقارنة',
      confirm: 'تأكيد القيمة الجديدة',
      keep: 'الإبقاء على القيمة الحالية',
      compareTitle: 'مقارنة القيم',
      current: 'الحالية',
      newer: 'رسالة أحدث',
      none: 'لا شيء',
    },
    confirmReview: {
      title: 'تأكيد «{{label}}» الجديدة؟',
      body: 'تحلّ القيمة الجديدة محل الحالية، وتبقى الحالية في السجل.',
      done: 'تم تأكيد القيمة الجديدة.',
    },
    reject: {
      title: 'الإبقاء على القيمة الحالية؟',
      body: 'تُحذف القيمة الأحدث ولا تُستخدم. يُحفظ سجل للحذف.',
    },
    outdated: {
      title: 'وضع علامة «قديمة» على هذه الذكرى؟',
      body: 'لن تُستخدم في الردود بعد الآن. تبقى في السجل حتى تُحذف أو تنتهي مدتها.',
      submit: 'وضع علامة «قديمة»',
      done: 'وُضعت علامة «قديمة».',
    },
    delete: {
      title: 'حذف هذه الذكرى؟',
      intro: 'سيؤدي هذا إلى إزالة الذكرى التالية من الاستخدام:',
      body: 'لن تُستخدم بعد الآن في ردود العملاء، لكن سيُحفظ سجل تدقيق.',
      bodyPending: 'تُزال القيمة التي تنتظر المراجعة. سيُحفظ سجل تدقيق.',
      submit: 'إزالة من الاستخدام',
    },
    erase: {
      title: 'محو كل ما نتذكره عن {{name}}؟',
      body: 'تُحذف كل القيم المحفوظة ({{total}}) وسجلها، مع فهارس البحث.',
      kept: 'يبقى سجل المحادثة وسجل التدقيق وسجل الحذف.',
      submit: 'محو كل الذاكرة',
      turnOff: 'إيقاف الذاكرة لهذا العميل أيضًا',
      turnOffHint: 'لن يُحفظ شيء جديد حتى يوافق العميل مرة أخرى.',
    },
    notice: {
      removed: 'أُزيلت من الاستخدام.',
      erased: 'مُحيت كل ذاكرة هذا العميل.',
      pending: 'يجري تحديث الفهارس المرتبطة. قد يستغرق ذلك بضع دقائق.',
      completed: 'مُسحت الفهارس والذاكرة المؤقتة. اكتمل الحذف.',
    },
    deletions: {
      title: 'عمليات الحذف وسجل التدقيق',
      description: 'ما حُذف، ومن طلب ذلك، وما بقي.',
      steps: 'خطوات الحذف',
      line: '{{who}} · {{reason}} · {{time}}',
      kept: 'بقي: {{items}}.',
      status: {
        pending: 'قيد التنفيذ',
        completed: 'مكتمل',
      },
      scope: {
        record: 'حُذفت ذكرى (السجلات: {{total}})',
        customer: 'مُحيت كل الذاكرة (السجلات: {{total}})',
      },
      requester: {
        customer: 'العميل',
        staff: 'موظف',
        system: 'Hamnivo',
      },
      reasons: {
        customer_request: 'طلب العميل',
        staff_request: 'طلب الموظفين',
        erasure_request: 'طلب محو',
        retention_expired: 'انتهت مدة الاحتفاظ',
        review_rejected: 'رُفضت في المراجعة',
      },
      stepNames: {
        records: 'السجلات',
        embeddings: 'فهرس البحث',
        citations: 'الاستشهادات',
        caches: 'الذاكرة المؤقتة',
        verification: 'التحقق',
      },
      keptNames: {
        audit_log: 'سجل التدقيق',
        consent_log: 'اختيارات الذاكرة',
        deletion_record: 'سجل الحذف',
        chat_history: 'سجل المحادثة',
      },
    },
  },
  access: {
    settings: 'الإعدادات',
    you: 'أنت',
    anyone: 'أي شخص',
    copyFailed: 'لم ينجح النسخ. حدّد النص وانسخه بنفسك.',
    copyValue: 'نسخ {{label}}',
    scope: {
      tenant: 'مساحة العمل كلها',
      workspace: 'منطقة العمل: {{name}}',
      brand: 'العلامة التجارية: {{name}}',
      team: 'الفريق: {{name}}',
      inbox: 'صندوق وارد',
      unknown: 'جزء من مساحة العمل',
    },
    scopeTypes: {
      tenant: 'مساحة العمل كلها',
      workspace: 'منطقة عمل واحدة',
      brand: 'علامة تجارية واحدة',
      team: 'فريق واحد',
    },
    team: {
      title: 'الفريق والصلاحيات',
      description: 'حدّد من يستطيع استخدام مساحة العمل هذه، وأدوارهم وما يُسمح لهم بفعله.',
      tabs: { members: 'الأعضاء', roles: 'الأدوار', audit: 'سجل التدقيق' },
      invite: 'دعوة عضو',
      search: 'البحث في الأعضاء',
      searchPlaceholder: 'الاسم أو البريد الإلكتروني',
      activeCount: 'الأعضاء النشطون: {{n}}',
      loading: 'جارٍ تحميل الأعضاء',
      noMatches: 'لا يوجد أعضاء يطابقون «{{query}}».',
      clearSearch: 'مسح البحث',
      columns: {
        name: 'الاسم',
        role: 'الدور',
        teams: 'الفرق',
        lastActive: 'آخر نشاط',
        status: 'الحالة',
        actions: 'الإجراءات',
      },
      status: {
        active: 'نشط',
        suspended: 'موقوف',
        invited: 'دعوة معلّقة',
        expired: 'انتهت الدعوة',
        removed: 'أُزيل',
      },
      noTeams: 'بلا فريق',
      noRole: 'بلا دور',
      notYet: 'ليس بعد',
      invitedOn: 'دُعي {{time}}',
      until: 'حتى {{time}}',
      actionsFor: 'إجراءات لـ {{name}}',
      changeAccess: 'تغيير الدور أو النطاق',
      suspend: 'إيقاف',
      restore: 'إعادة الصلاحيات',
      remove: 'إزالة من مساحة العمل',
      resend: 'إعادة إرسال الدعوة',
      revokeInvite: 'إلغاء الدعوة',
      beyondReach: 'صلاحياته تتجاوز صلاحياتك، لذلك لا يمكنك تغييرها.',
      selfNote: 'لا يمكنك تغيير صلاحياتك من هنا.',
      suspendTitle: 'هل تريد إيقاف {{name}}؟',
      suspendBody:
        'يفقد الوصول إلى مساحة العمل هذه فورًا، ويُسجَّل خروجه إن لم تكن له مساحة عمل أخرى. تبقى محادثاته كما هي. يمكنك إعادته لاحقًا.',
      suspendSubmit: 'إيقاف',
      suspended: 'أُوقف {{name}}.',
      restoreTitle: 'هل تريد إعادة {{name}}؟',
      restoreBody: 'يستعيد الأدوار التي كانت لديه. يسجّل الدخول مرة أخرى للمتابعة.',
      restoreSubmit: 'إعادة',
      restored: 'عاد {{name}} إلى الوصول.',
      revokeInviteTitle: 'هل تريد إلغاء دعوة {{email}}؟',
      revokeInviteBody: 'يتوقف الرابط الموجود في بريده عن العمل.',
      revokeInviteSubmit: 'إلغاء الدعوة',
      inviteRevoked: 'أُلغيت دعوة {{email}}.',
      resent: 'أُرسل رابط دعوة جديد إلى {{email}}. لم يعد الرابط القديم يعمل.',
      recent: 'النشاط الأخير',
      recentLoading: 'جارٍ تحميل النشاط الأخير',
      recentEmpty: 'لم يحدث شيء بعد.',
      viewAudit: 'فتح سجل التدقيق',
    },
    inviteDialog: {
      title: 'دعوة عضو',
      description: 'يصله بريد فيه رابط للانضمام إلى مساحة العمل هذه.',
      email: 'البريد الإلكتروني',
      emailInvalid: 'أدخل بريدًا إلكترونيًا مثل name@example.com.',
      role: 'الدور',
      roleBeyond: '{{name}} (يتجاوز صلاحياتك)',
      where: 'أين يسري الدور',
      workspace: 'منطقة العمل',
      team: 'الفريق',
      brand: 'العلامة التجارية',
      pick: 'اختر واحدًا',
      pickRequired: 'اختر أين يسري الدور.',
      expires: 'تنتهي الصلاحية في',
      expiresHint: 'اتركه فارغًا لتبقى الصلاحية حتى يغيّرها أحد.',
      submit: 'إرسال الدعوة',
      sent: 'أُرسلت الدعوة إلى {{email}}. يعمل الرابط لمدة 7 أيام.',
      copyLink: 'نسخ رابط الدعوة',
      linkHint: 'إن لم يكن البريد مُعدًّا على هذا الخادم، فأرسل إليه هذا الرابط بنفسك.',
      done: 'تم',
    },
    changeDialog: {
      title: 'صلاحيات {{name}}',
      description: 'يسري كل دور حيث مُنح. تسري التغييرات مع طلبه التالي.',
      current: 'الأدوار الحالية',
      none: 'لا أدوار. يستطيع تسجيل الدخول لكنه لا يستطيع فعل شيء هنا.',
      change: 'تغيير',
      removeRole: 'إزالة الدور',
      removeRoleLabel: 'إزالة {{role}} من {{name}}',
      removeRoleTitle: 'هل تريد إزالة {{role}} من {{name}}؟',
      removeRoleBody: 'يفقد ما يتيحه هذا الدور مع طلبه التالي.',
      add: 'إضافة دور آخر',
      editTitle: 'تغيير {{role}}',
      addTitle: 'إضافة دور',
      reason: 'السبب',
      reasonHint: 'يُحفظ في سجل التدقيق.',
      save: 'حفظ الصلاحيات',
      saved: 'تغيّرت صلاحيات {{name}}.',
      back: 'العودة إلى الأدوار',
    },
    offboard: {
      title: 'هل تريد إزالة {{name}}؟',
      loading: 'جارٍ التحقق مما لديه',
      body: 'يفقد الوصول إلى مساحة العمل هذه فورًا. تُزال أدواره، ويغادر فرقه، وتتوقف الدعوات المعلّقة لعنوانه. يبقى عمله السابق في السجل.',
      conversations: 'المحادثات المفتوحة المسندة إليه: {{n}}.',
      noConversations: 'ليست لديه محادثات مفتوحة.',
      reassign: 'أعد إسناد محادثاته المفتوحة أولًا',
      reassignTo: 'إعادة الإسناد إلى',
      queue: 'إعادتها إلى قائمة انتظار الفريق',
      cantReassign:
        'لا يمكنك إعادة إسناد المحادثات. تبقى بانتظاره حتى يعيد إسنادها شخص لديه الصلاحية.',
      reason: 'السبب',
      reasonHint: 'يُحفظ في سجل التدقيق.',
      submit: 'إزالة {{name}}',
      reassigning: 'جارٍ إعادة إسناد المحادثات',
      reassigned: 'المحادثات المعاد إسنادها: {{n}}.',
      reassignFailed: 'محادثات تعذّرت إعادة إسنادها: {{n}}. لم تتم إزالة {{name}}.',
      removed: 'أُزيل {{name}} من مساحة العمل هذه.',
      ownerTitle: 'عيّن مالكًا آخر قبل إزالة هذا المالك',
      ownerBody:
        'يجب أن يكون لمساحة العمل مالك واحد على الأقل دائمًا. اجعل شخصًا آخر مالكًا أولًا، ثم حاول مرة أخرى.',
      goToMembers: 'الذهاب إلى الأعضاء',
    },
    roles: {
      intro:
        'تغطي الأدوار المضمّنة المهام الشائعة. تمنح الأدوار المخصصة الصلاحيات التي تختارها بالضبط.',
      new: 'دور جديد',
      loading: 'جارٍ تحميل الأدوار',
      builtIn: 'مضمّن',
      custom: 'مخصص',
      members: 'الأعضاء: {{n}}',
      permissions: 'الصلاحيات: {{n}}',
      open: 'فتح {{name}}',
      readOnly: 'للقراءة فقط',
      editorNew: 'دور جديد',
      summary: '{{kind}} · {{members}}',
      name: 'الاسم',
      key: 'المفتاح',
      keyHint: 'أحرف إنجليزية صغيرة وأرقام و_، يُستخدم في واجهة البرمجة. لا يمكن تغييره لاحقًا.',
      keyInvalid: 'استخدم من 2 إلى 40 حرفًا صغيرًا أو رقمًا أو _، يبدأ بحرف.',
      nameRequired: 'أدخل اسمًا.',
      description: 'الغرض من هذا الدور',
      permissionsLegend: 'الصلاحيات',
      pickOne: 'اختر صلاحية واحدة على الأقل.',
      notGrantable: 'خارج صلاحياتك',
      stepUp: 'يتطلب تسجيل دخول حديثًا',
      mfa: 'يتطلب التحقق بخطوتين',
      presetNote: 'لا يمكن تغيير الأدوار المضمّنة. انسخ أحدها لإنشاء دور مخصص يمكنك تعديله.',
      beyondNote: 'يتضمن هذا الدور صلاحيات ليست لديك، لذلك لا يمكنك تغييره.',
      duplicate: 'نسخه كدور مخصص',
      copyName: '{{name}} (نسخة)',
      preview: 'ما الذي يغيّره الحفظ',
      previewLoading: 'جارٍ التحقق من التغيير',
      previewNothing: 'لا شيء يتغيّر بعد.',
      adds: 'يضيف',
      removes: 'يزيل',
      sensitive: 'يحتاج إلى تحقق إضافي: {{list}}',
      outside: 'لا يمكنك تضمين: {{list}}',
      impact: 'من يتأثر',
      impactNone: 'لا أحد يحمل هذا الدور بعد.',
      gains: 'يكتسب {{list}}',
      loses: 'يفقد {{list}}',
      unchanged: 'بلا تغيير',
      save: 'حفظ التغييرات',
      create: 'إنشاء الدور',
      saved: 'حُفظ الدور {{name}}.',
      created: 'أُنشئ الدور {{name}}.',
      conflict:
        'غيّر شخص آخر هذا الدور أثناء تعديلك. قارن بنسخته: تبقى تغييراتك، وتعرض المعاينة ما سيغيّره الحفظ في نسخته.',
      reload: 'المقارنة بنسخته',
      archive: 'إحالة الدور إلى التقاعد',
      archiveTitle: 'هل تريد إحالة {{name}} إلى التقاعد؟',
      archiveBody:
        'يختفي من القائمة. تحتفظ المنح السابقة وسجل التدقيق باسمه. لا يمكن إحالة إلا دور لا يحمله أحد.',
      archiveSubmit: 'إحالة الدور إلى التقاعد',
      archived: 'أُحيل الدور {{name}} إلى التقاعد.',
      heldNote: 'هناك من يحمل هذا الدور. غيّر صلاحياتهم قبل إحالته إلى التقاعد.',
      close: 'إغلاق الدور',
      discardTitle: 'هل تريد تجاهل تغييراتك على هذا الدور؟',
      discardBody: 'ما غيّرته هنا لم يُحفظ بعد.',
    },
    audit: {
      intro:
        'كل تغيير في الصلاحيات والإعدادات والبيانات، بالترتيب. لا يمكن تعديل الإدخالات أو حذفها.',
      loading: 'جارٍ تحميل سجل التدقيق',
      filters: 'عوامل التصفية',
      action: 'الإجراء',
      actionPlaceholder: 'مثلًا member.removed',
      actor: 'من',
      target: 'معرّف الهدف',
      targetPlaceholder: 'مثلًا stf_…',
      from: 'من تاريخ',
      to: 'إلى تاريخ',
      apply: 'تطبيق عوامل التصفية',
      clear: 'مسح عوامل التصفية',
      applied: 'المعروض: {{list}}',
      empty: 'لا توجد إدخالات تطابق عوامل التصفية هذه.',
      columns: {
        time: 'الوقت',
        actor: 'من',
        action: 'ماذا',
        target: 'الهدف',
        outcome: 'النتيجة',
        details: 'التفاصيل',
      },
      outcomes: { success: 'تم', denied: 'مرفوض', failed: 'فشل' },
      actorTypes: {
        staff: 'عضو في الفريق',
        service_account: 'مفتاح واجهة برمجة',
        customer: 'عميل',
        ai: 'الذكاء الاصطناعي',
        system: 'Hamnivo',
        platform: 'دعم Hamnivo',
      },
      reason: 'السبب: {{reason}}',
      showData: 'عرض البيانات المسجّلة',
      loadMore: 'تحميل إدخالات أقدم',
      verify: 'التحقق من السلامة',
      verifyOk: 'سجل التدقيق سليم. الإدخالات المفحوصة: {{n}}.',
      verifyBroken: 'سجل التدقيق معطوب عند الإدخال {{seq}}. تواصل مع الدعم واذكر هذا الرقم.',
      export: 'تصدير',
      exportCsv: 'تصدير بصيغة CSV (جداول البيانات)',
      exportNdjson: 'تصدير بصيغة NDJSON (مع قيم التجزئة)',
      exporting: 'جارٍ تجهيز التصدير',
      exported: 'نُزّل التصدير. وقد سُجّل في سجل التدقيق.',
      exportNote: 'يتبع التصدير عوامل التصفية أعلاه ويُسجَّل في سجل التدقيق.',
      forbiddenExport: 'يتطلب التصدير صلاحية تصدير سجل التدقيق.',
    },
    auditActions: {
      tenant: { created: 'أُنشئت مساحة العمل' },
      member: {
        suspended: 'أُوقف عضو',
        restored: 'أُعيد عضو',
        removed: 'أُزيل عضو',
        sessions_ended: 'أُنهيت الجلسات',
        joined_by_sso: 'انضم بالدخول الموحّد',
        sso_join_skipped: 'تُخطّي الانضمام بالدخول الموحّد',
      },
      invitation: {
        created: 'أُرسلت دعوة',
        resent: 'أُعيد إرسال دعوة',
        revoked: 'أُلغيت دعوة',
        accepted: 'قُبلت دعوة',
      },
      grant: { created: 'مُنح دور', revoked: 'أُزيل دور', replaced: 'تغيّر دور' },
      role: { created: 'أُنشئ دور', updated: 'تغيّر دور', archived: 'أُحيل دور إلى التقاعد' },
      audit: { exported: 'صُدّر سجل التدقيق' },
      sso: {
        domain_added: 'أُضيف نطاق',
        domain_verified: 'تُحقّق من نطاق',
        domain_check_failed: 'فشل التحقق من نطاق',
        domain_removed: 'أُزيل نطاق',
        connection_created: 'رُبط الدخول الموحّد',
        connection_updated: 'تغيّر الدخول الموحّد',
        connection_removed: 'أُزيل الدخول الموحّد',
        sign_in_refused: 'رُفض دخول موحّد',
      },
      operator: {
        appointed: 'عُيّن مشغّل منصة',
        role_changed: 'تغيّر دور مشغّل منصة',
        disabled: 'عُطّل مشغّل منصة',
      },
      incident: { declared: 'أُعلن عن حادث', resolved: 'حُلّ حادث' },
      support: {
        requested: 'طلب الدعم الوصول',
        approved: 'قُبل وصول الدعم',
        declined: 'رُفض وصول الدعم',
        revoked: 'أُلغي وصول الدعم',
        ended: 'انتهى وصول الدعم',
        accessed: 'قرأ الدعم بيانات',
      },
    },
    security: {
      title: 'الأمان',
      description: 'الدخول الموحّد لنطاق بريدك الإلكتروني، والوصول المؤقت لفريق دعم Hamnivo.',
      sso: {
        title: 'الدخول الموحّد (SSO)',
        description:
          'يسجّل أصحاب العناوين في نطاقك الدخول عبر مزوّد الهوية الخاص بك (OpenID Connect).',
        loading: 'جارٍ تحميل إعدادات الدخول الموحّد',
        forbidden: 'يدير مالكو مساحة العمل ومسؤولوها الدخول الموحّد.',
        steps: 'أثبت ملكيتك للنطاق، واربط مزوّد الهوية، ثم فعّله.',
        domains: 'نطاقات البريد الإلكتروني',
        addDomain: 'إضافة نطاق',
        domain: 'النطاق',
        domainHint: 'الجزء الذي يلي @ في عناوين فريقك، مثل northline.example.',
        domainInvalid: 'أدخل نطاقًا مثل northline.example.',
        noDomains: 'لا توجد نطاقات بعد.',
        verified: 'موثّق',
        pending: 'بانتظار سجل DNS',
        publish: 'انشر سجل TXT هذا لدى مزوّد DNS، ثم تحقّق منه. قد يستغرق ظهور السجلات بعض الوقت.',
        recordName: 'الاسم',
        recordValue: 'القيمة',
        check: 'تحقّق الآن',
        checked: 'آخر تحقق {{time}}',
        results: {
          verified: 'وُجد السجل. النطاق موثّق.',
          not_found: 'لم يُعثر على السجل بعد. تحقّق من الاسم والقيمة، ثم حاول لاحقًا.',
          lookup_failed: 'فشل استعلام DNS. حاول مرة أخرى بعد بضع دقائق.',
          claimed_elsewhere:
            'وثّقت مساحة عمل أخرى هذا النطاق بالفعل. تواصل مع دعم Hamnivo إن كان ذلك خطأ.',
        },
        removeDomain: 'إزالة النطاق',
        removeDomainLabel: 'إزالة {{domain}}',
        removeDomainTitle: 'هل تريد إزالة {{domain}}؟',
        removeDomainBody: 'يمكنك إضافته لاحقًا، لكن عليك توثيقه مرة أخرى.',
        connection: 'مزوّد الهوية لـ {{domain}}',
        connectHint: 'أنشئ تطبيق OpenID Connect لدى مزوّدك، ثم أدخل تفاصيله هنا.',
        issuer: 'عنوان المُصدِر (Issuer URL)',
        issuerHint: 'مثلًا https://login.example.com. يقرأ Hamnivo مستند الاكتشاف الخاص به.',
        issuerInvalid: 'أدخل عنوان المُصدِر، ويبدأ بـ https://.',
        clientId: 'معرّف العميل (Client ID)',
        clientSecret: 'سرّ العميل (Client secret)',
        clientSecretHint: 'يُخزَّن مشفّرًا. لا يُعرض مجددًا، بل آخر أحرفه فقط.',
        required: 'املأ هذا الحقل.',
        save: 'حفظ الربط',
        created: 'تم الربط. سجّل عنوان إعادة التوجيه لدى مزوّدك، ثم فعّل الربط.',
        redirectUri: 'عنوان إعادة التوجيه (Redirect URI)',
        redirectUriHint: 'سجّل هذا العنوان لدى مزوّد الهوية.',
        secretMasked: 'سرّ العميل {{masked}}',
        rotated: 'استُبدل {{time}}',
        replaceSecret: 'استبدال سرّ العميل',
        replaceTitle: 'استبدال سرّ العميل',
        replaceSubmit: 'استبدال السرّ',
        replaced: 'استُبدل سرّ العميل.',
        endpoints: 'نقاط النهاية التي وُجدت لدى المزوّد',
        status: { draft: 'غير مفعّل بعد', active: 'مفعّل', disabled: 'متوقف' },
        turnOn: 'تفعيل',
        turnOff: 'إيقاف',
        turnedOn: 'الدخول الموحّد مفعّل لـ {{domain}}.',
        turnedOff: 'الدخول الموحّد متوقف لـ {{domain}}. تعمل كلمات المرور مجددًا.',
        ssoOnly: 'اشتراط الدخول الموحّد',
        ssoOnlyHint:
          'على أصحاب العناوين في {{domain}} استخدام الدخول الموحّد. تتوقف لديهم كلمات المرور ومفاتيح المرور وإنشاء الحسابات وإعادة تعيين كلمة المرور.',
        ssoOnlyNeedsOn: 'فعّل الربط أولًا.',
        ssoOnlySaved: 'أصبح الدخول الموحّد مشروطًا لـ {{domain}}.',
        ssoOnlyOff: 'تعمل كلمات المرور ومفاتيح المرور مجددًا لـ {{domain}}.',
        jit: 'الأشخاص الجدد الذين يدخلون بالدخول الموحّد',
        jitHint:
          'اختر دورًا لإضافتهم تلقائيًا، أو اتركه متوقفًا للسماح للمدعوين فقط. لا يمكن اختيار أدوار تتطلب التحقق بخطوتين.',
        jitOff: 'المدعوون فقط',
        jitSave: 'حفظ',
        jitSaved: 'ينضم الأشخاص الجدد الآن بدور {{role}}.',
        jitCleared: 'لا ينضم الآن إلا المدعوون.',
        removeConnection: 'إزالة الربط',
        removeConnectionTitle: 'هل تريد إزالة الدخول الموحّد لـ {{domain}}؟',
        removeConnectionBody:
          'لن يتمكن أصحاب العناوين في {{domain}} من الدخول الموحّد بعد الآن. يمكنهم إعادة تعيين كلمة المرور إن كانت لديهم.',
        removed: 'أُزيل الدخول الموحّد لـ {{domain}}.',
      },
      support: {
        title: 'وصول الدعم',
        description:
          'لا يستطيع دعم Hamnivo قراءة جزء من مساحة العمل هذه إلا بعد موافقتك، ولمدة محدودة. الوصول للقراءة فقط، وتُسجَّل كل قراءة في سجل التدقيق.',
        loading: 'جارٍ تحميل وصول الدعم',
        forbidden: 'يوافق مالكو مساحة العمل على وصول الدعم.',
        pending: 'بانتظار قرارك',
        active: 'نشط الآن',
        history: 'طلبات سابقة',
        none: 'لا توجد طلبات من دعم Hamnivo.',
        from: '{{name}}، دعم Hamnivo',
        asked: 'طُلب {{time}}',
        reason: 'السبب: {{reason}}',
        areas: 'ما يُسمح له بقراءته',
        minutes: 'لأي مدة',
        duration: '{{minutes}} دقيقة',
        note: 'ملاحظة للدعم',
        approve: 'موافقة',
        decline: 'رفض',
        approved: 'قُبل وصول الدعم حتى {{time}}.',
        declined: 'رُفض الطلب.',
        revoke: 'إلغاء الآن',
        revokeTitle: 'هل تريد إلغاء وصول الدعم الآن؟',
        revokeBody: 'تُرفض قراءته التالية. يمكنه الطلب مرة أخرى إن ظل بحاجة إلى الوصول.',
        revoked: 'أُلغي وصول الدعم.',
        endsAt: 'ينتهي {{time}}',
        pickArea: 'اختر جزءًا واحدًا على الأقل.',
        states: {
          pending: 'بانتظار القرار',
          active: 'نشط',
          declined: 'مرفوض',
          revoked: 'ملغى',
          ended: 'منتهٍ',
          expired: 'انتهت مدته',
        },
        scopes: {
          configuration: 'الإعدادات (الأعضاء والأدوار والعلامات التجارية والوكلاء)',
          conversations: 'قائمة المحادثات (الحالة فقط، دون الرسائل أبدًا)',
          audit: 'سجل التدقيق',
        },
      },
    },
    notices: {
      label: 'إشعارات مساحة العمل',
      supportActive: 'يستطيع {{name}} من دعم Hamnivo قراءة {{areas}} حتى {{time}}.',
      supportRecorded: 'تُسجَّل كل قراءة في سجل التدقيق.',
      review: 'مراجعة',
      supportPending: 'طلب دعم Hamnivo وصولًا مؤقتًا. الطلبات المنتظرة: {{n}}.',
      reviewRequest: 'مراجعة الطلب',
      incident: {
        notice: 'إشعار',
        degraded: 'الخدمة متدهورة',
        outage: 'انقطاع الخدمة',
      },
      since: 'منذ {{time}}',
      channelDown:
        'قناة {{channel}} الخاصة بـ {{brand}} لا تعمل منذ {{time}}. قد لا تصل الرسائل الجديدة إلى فريقك.',
      channelsDown: 'قنوات توقفت عن العمل: {{n}}. قد لا تصل الرسائل الجديدة إلى فريقك.',
      channelItem: 'قناة {{channel}} الخاصة بـ {{brand}}، منذ {{time}}',
      fixChannel: 'أصلحها',
    },
    invitation: {
      title: 'الانضمام إلى مساحة عمل',
      description: 'دُعيت إلى مساحة عمل في Hamnivo.',
      signInFirst: 'سجّل الدخول بالعنوان الذي أُرسلت إليه الدعوة، ثم اقبلها.',
      signIn: 'سجّل الدخول للقبول',
      createAccount: 'إنشاء حساب',
      createHint:
        'جديد على Hamnivo؟ أنشئ حسابًا بالعنوان المدعو، وأكّده، ثم افتح رابط الدعوة مرة أخرى.',
      signedInAs: 'مسجّل الدخول باسم {{email}}',
      accept: 'قبول الدعوة',
      accepted: 'انضممت إلى {{name}}.',
      missing: 'لا يحتوي هذا الرابط على دعوة. افتح الرابط من بريدك مرة أخرى.',
      invalid:
        'لم تعد هذه الدعوة صالحة. ربما انتهت مدتها أو استُخدمت أو أُرسلت إلى عنوان آخر. اطلب دعوة جديدة.',
      unverified: 'أكّد بريدك الإلكتروني أولًا. أرسلنا إليك رابطًا عند إنشاء الحساب.',
      otherAccount: 'تسجيل الدخول بحساب آخر',
    },
  },
  sso: {
    continue: 'المتابعة بالدخول الموحّد',
    short: 'المتابعة بـ SSO',
    email: 'بريد العمل الإلكتروني',
    emailHint: 'ننقلك إلى صفحة تسجيل الدخول في مؤسستك.',
    submit: 'متابعة',
    back: 'العودة إلى الدخول بكلمة المرور',
    notSetUp: 'الدخول الموحّد غير مُعدّ لهذا العنوان. سجّل الدخول بكلمة المرور، أو اسأل المسؤول لديك.',
    required: 'تشترط مؤسستك الدخول الموحّد لهذا العنوان.',
    popupBlocked:
      'حظر متصفحك نافذة تسجيل الدخول. اسمح بالنوافذ المنبثقة لـ Studio ثم حاول مرة أخرى.',
    failed: 'لم يكتمل الدخول الموحّد. حاول مرة أخرى.',
    errors: {
      sso_email_unverified: 'لم يوثّق مزوّد الهوية بريدك الإلكتروني.',
      sso_domain_mismatch: 'لا يستطيع مزوّد الهوية هذا تسجيل دخول إلا العناوين في نطاقه.',
      sso_unverified_local_account:
        'يوجد حساب Hamnivo بهذا العنوان لكنه لم يُؤكَّد قط. أكّده أولًا، ثم استخدم الدخول الموحّد.',
      sso_unavailable: 'الدخول الموحّد غير متاح لهذا العنوان حاليًا.',
      access_denied: 'أُلغي تسجيل الدخول لدى مزوّد الهوية.',
      generic: 'لم ينجح الدخول الموحّد. حاول مرة أخرى، أو تواصل مع المسؤول لديك.',
    },
    done: 'تم تسجيل الدخول. يمكنك إغلاق هذه النافذة.',
  },
  reauthMore: {
    otherWays: 'أو أكّد بطريقة أخرى',
    passkey: 'استخدام مفتاح مرور',
    sso: 'استخدام الدخول الموحّد',
    ssoDescription:
      'يتطلب هذا الإجراء تسجيل دخول حديثًا. تابع عبر الدخول الموحّد لمؤسستك. يبقى عملك في هذه الصفحة كما هو.',
  },
  platform: {
    title: 'منصة Hamnivo',
    notOperator: 'هذه الواجهة مخصصة لمشغّلي منصة Hamnivo.',
    notOperatorHint: 'حسابك ليس حساب مشغّل منصة. أدوار مساحات العمل لا تمنح الوصول إلى هنا.',
    returnToWorkspace: 'العودة إلى مساحة العمل',
    nav: {
      label: 'المنصة',
      tenants: 'المستأجرون',
      health: 'سلامة الخدمات',
      support: 'وصول الدعم',
      incidents: 'الحوادث',
      audit: 'سجل التدقيق',
    },
    roles: {
      platform_admin: 'مسؤول المنصة',
      support_operator: 'مشغّل دعم',
      platform_viewer: 'مُطّلع على المنصة',
    },
    mfaNeeded: 'فعّل التحقق بخطوتين في أمان الحساب لتطلب وصول الدعم أو تستخدمه.',
    overview: {
      tenants: 'المستأجرون',
      active: 'النشطون: {{n}}',
      supportActive: 'وصول دعم نشط',
      supportPending: 'بانتظار قرار',
      aiStops: 'إيقافات الذكاء الاصطناعي',
      providersOff: 'مزوّدون متوقفون',
    },
    tenants: {
      search: 'البحث في المستأجرين',
      searchPlaceholder: 'الاسم أو المعرّف',
      loading: 'جارٍ تحميل المستأجرين',
      empty: 'لا يوجد مستأجرون مطابقون.',
      columns: {
        name: 'المستأجر',
        status: 'الحالة',
        region: 'المنطقة',
        aiStops: 'إيقافات الذكاء الاصطناعي',
        support: 'وصول الدعم',
      },
      open: 'فتح {{name}}',
      status: { active: 'نشط', suspended: 'موقوف', closing: 'قيد الإغلاق' },
      detail: 'تفاصيل المستأجر',
      id: 'معرّف المستأجر',
      created: 'أُنشئ',
      region: 'المنطقة',
      locale: 'اللغة الافتراضية',
      counts: 'مناطق العمل: {{workspaces}} · العلامات التجارية: {{brands}}',
      connectors: 'الموصِّلات',
      connectorHealth: 'آخر فحص سلامة {{time}}',
      noConnectors: 'لا توجد موصِّلات.',
      aiStops: 'إيقافات الذكاء الاصطناعي',
      noAiStops: 'الذكاء الاصطناعي غير موقوف في أي مكان.',
      aiStopScope: { tenant: 'المستأجر كله', brand: 'علامة تجارية واحدة', agent: 'وكيل واحد' },
      providers: 'مزوّدو النماذج',
      noProviders: 'لا توجد اتصالات بمزوّدين.',
      providerStatus: { active: 'مفعّل', disabled: 'متوقف' },
      contentNote: 'يرى مشغّلو المنصة البيانات الوصفية فقط. محتوى العملاء يتطلب وصول الدعم.',
      noAccess: 'لا وصول إلى محتوى العملاء',
      noAccessHint: 'يجب أن توافق مساحة العمل على طلب أولًا. الوصول للقراءة فقط ومحدود المدة.',
      requestAccess: 'طلب وصول مؤقت',
      grants: 'وصول الدعم لهذا المستأجر',
    },
    request: {
      title: 'طلب وصول مؤقت إلى {{name}}',
      description: 'يصل بريد إلى مالكي مساحة العمل ويقررون. لا يُشارك شيء حتى يوافقوا.',
      reason: 'السبب',
      reasonHint: 'اذكر ما تحتاج إلى التحقق منه ولماذا. ترى مساحة العمل هذا النص.',
      reasonShort: 'اكتب 10 أحرف على الأقل.',
      areas: 'ما تحتاج إلى قراءته',
      minutes: 'لأي مدة',
      submit: 'إرسال الطلب',
      sent: 'أُرسل الطلب. يظهر ضمن وصول الدعم عندما تقرر مساحة العمل.',
    },
    support: {
      intro: 'طلباتك للوصول المؤقت للقراءة فقط إلى بيانات المستأجرين.',
      loading: 'جارٍ تحميل وصول الدعم',
      none: 'لم تطلب وصولًا بعد.',
      expiresIn: 'ينتهي الوصول بعد {{minutes}} دقيقة',
      endsAt: 'ينتهي {{time}}',
      end: 'إنهاء الجلسة',
      ended: 'انتهى وصول الدعم.',
      approvedBy: 'وافق عليه {{name}}',
      declinedBy: 'رفضه {{name}}',
      note: 'ملاحظة: {{note}}',
      requestAgain: 'الطلب مرة أخرى',
      view: {
        configuration: 'عرض الإعدادات',
        conversations: 'عرض قائمة المحادثات',
        audit: 'عرض سجل التدقيق',
      },
      reading: 'جارٍ تحميل {{area}}',
      members: 'الأعضاء',
      roles: 'الأدوار',
      brands: 'العلامات التجارية',
      agents: 'وكلاء الذكاء الاصطناعي',
      live: 'منشور',
      notLive: 'غير منشور',
      conversationsNote: 'الحالة فقط. لا تُشارك الرسائل مع الدعم أبدًا.',
    },
    health: {
      checked: 'فُحص {{time}}',
      loading: 'جارٍ فحص الخدمات',
      services: {
        api: 'واجهة البرمجة',
        database: 'قاعدة البيانات',
        valkey: 'الذاكرة المؤقتة والطوابير',
        queue: 'التسليم في الخلفية',
        connectors: 'الموصِّلات',
      },
      states: { healthy: 'سليم', degraded: 'متدهور', down: 'متوقف' },
      queue: 'أحداث منتظرة: {{pending}} · متعثرة: {{failing}}',
      oldest: 'أقدمها منتظر منذ: {{seconds}} ث',
      connectors: 'موصِّلات: {{total}} · تحتاج انتباهًا: {{degraded}} · متوقفة: {{disabled}}',
      providers: 'مفاتيح نماذج المنصة',
      configured: 'مُعدّ',
      notConfigured: 'غير مُعدّ',
    },
    incidents: {
      intro: 'تظهر الحوادث شريطًا في كل مساحة عمل حتى تُحل.',
      declare: 'الإعلان عن حادث',
      loading: 'جارٍ تحميل الحوادث',
      none: 'لا توجد حوادث.',
      active: 'نشط',
      resolved: 'حُلّ {{time}}',
      started: 'بدأ {{time}}',
      resolve: 'حلّ',
      resolvedDone: 'حُلّ الحادث واختفى الشريط.',
      severity: 'الخطورة',
      severities: { notice: 'إشعار', degraded: 'متدهور', outage: 'انقطاع' },
      titleField: 'العنوان',
      message: 'رسالة لمساحات العمل',
      messageHint: 'اذكر ما تأثر وما زال يعمل.',
      tooShort: 'اكتب 3 أحرف على الأقل.',
      submit: 'نشر الحادث',
      posted: 'يظهر شريط الحادث في كل مساحة عمل.',
    },
    audit: {
      intro: 'كل ما يُفعل في هذه الواجهة، وكل قرار لمساحة عمل بشأن وصول الدعم.',
      loading: 'جارٍ تحميل سجل تدقيق المنصة',
      empty: 'لم يُسجَّل شيء بعد.',
      operator: 'المشغّل',
      workspaceDecision: 'قرار مساحة العمل',
      tenant: 'المستأجر',
      loadMore: 'تحميل إدخالات أقدم',
    },
  },
  permissionAreas: {
    agency: 'الوكالة',
    branding: 'العلامة التجارية',
    conversations: 'المحادثات',
    visitors: 'الزوار',
    inbox: 'صناديق الوارد والتوجيه',
    teams: 'الفرق',
    members: 'الأعضاء',
    roles: 'الأدوار',
    grants: 'الصلاحيات',
    audit: 'سجل التدقيق',
    brands: 'العلامات التجارية',
    widget: 'أداة الدردشة',
    agents: 'وكلاء الذكاء الاصطناعي',
    automations: 'الأتمتة',
    knowledge: 'المعرفة',
    tools: 'الأدوات والإجراءات',
    decisions: 'القرارات',
    memory: 'ذاكرة العملاء',
    commerce: 'الطلبات والتجارة',
    analytics: 'التقارير',
    billing: 'الفوترة',
    api_keys: 'مفاتيح واجهة البرمجة',
    webhooks: 'خطافات الويب',
    network: 'المضيفات المسموح بها',
    mcp: 'مساعدو الذكاء الاصطناعي (MCP)',
    integrations: 'التكاملات',
    secrets: 'بيانات الاعتماد',
    extensions: 'الإضافات',
    migration: 'الاستيراد',
    settings: 'إعدادات مساحة العمل',
    security: 'الأمان',
    privacy: 'الخصوصية',
    customers: 'بيانات العملاء',
    support: 'وصول الدعم',
  },
  permissions: {
    agency: {
      read: 'عرض إعدادات الوكالة ودليل العملاء',
      manage: 'إدارة إعدادات الوكالة وروابط العملاء المستقلين',
    },
    branding: {
      read: 'عرض علامة البوابة والنطاقات المخصصة',
      manage: 'إدارة علامة البوابة والنطاقات المخصصة الموثقة',
    },
    conversations: {
      read: 'قراءة المحادثات وسجل العميل',
      reply: 'الرد على العملاء',
      note: 'كتابة ملاحظات داخلية',
      assign: 'إسناد المحادثات ونقلها',
      takeover: 'تولّي محادثة من الذكاء الاصطناعي أو من شخص آخر',
      status: 'حلّ المحادثات وإعادة فتحها وتأجيلها',
      tag: 'وسم المحادثات وملء حقولها المخصصة',
      bulk: 'تغيير محادثات كثيرة دفعة واحدة',
      review: 'مراجعة محادثات الآخرين وتوجيههم',
      export: 'تصدير نصوص المحادثات',
    },
    visitors: { block: 'حظر الزوار الذين يسيئون استخدام المحادثة ورفع الحظر عنهم' },
    inbox: { manage: 'إعداد صناديق الوارد والتوجيه وساعات العمل' },
    teams: { manage: 'إنشاء الفرق وإدارة أعضائها' },
    members: {
      read: 'رؤية أعضاء الفريق وأدوارهم',
      invite: 'دعوة الأعضاء',
      remove: 'إيقاف الأعضاء أو إزالتهم',
    },
    roles: { manage: 'إنشاء الأدوار المخصصة وتعديلها' },
    grants: { manage: 'منح الأدوار وإلغاؤها ضمن نطاقك' },
    audit: { read: 'قراءة سجل التدقيق', export: 'تصدير سجل التدقيق' },
    brands: { manage: 'إنشاء العلامات التجارية وتحديد المواقع المسموح بها' },
    widget: {
      configure: 'إعداد أداة الدردشة',
      identity_keys: 'تسجيل المفاتيح التي تتحقق من هوية العملاء',
    },
    agents: {
      read: 'عرض وكلاء الذكاء الاصطناعي وإصداراتهم',
      manage: 'تعديل مسودات وكلاء الذكاء الاصطناعي',
      publish: 'نشر وكلاء الذكاء الاصطناعي أو التراجع عنهم',
    },
    automations: {
      read: 'عرض قواعد الأتمتة وما فعلته',
      manage: 'إنشاء قواعد الأتمتة ونشرها وإيقافها، ومنها رسائل الموقع',
    },
    knowledge: { read: 'قراءة مصادر المعرفة', manage: 'إضافة المعرفة وتعديلها وإيقافها' },
    tools: {
      read: 'عرض الأدوات وسياسات الإجراءات وإجراءات الذكاء الاصطناعي ونتائج المحاكاة',
      manage: 'إعداد الأدوات وسياسات الإجراءات',
      approve: 'الموافقة على الإجراءات التي يقترحها وكلاء الذكاء الاصطناعي',
    },
    decisions: {
      read: 'مراجعة قرارات التوجيه والجودة',
      manage: 'تعديل معايير القرارات وعتباتها',
    },
    memory: {
      read: 'قراءة سجلات ذاكرة العملاء',
      manage: 'تصحيح سجلات الذاكرة أو تقييدها',
      delete: 'حذف سجلات الذاكرة',
      erase: 'محو كل ما يُتذكّر عن عميل',
      export: 'تصدير كل ما يُتذكّر عن عميل',
    },
    commerce: {
      read: 'رؤية السلال وعروض الأسعار والطلبات والمدفوعات والمبالغ المستردة',
      carts: { manage: 'مشاركة المنتجات وإعداد عروض الأسعار في المحادثات' },
      orders: { manage: 'تسجيل تحديثات تنفيذ الطلبات' },
      refunds: {
        request: 'طلب استرداد مبلغ لعميل',
        approve: 'الموافقة على المبالغ المستردة أو رفضها',
      },
      exceptions: { resolve: 'حلّ استثناءات الدفع والاسترداد بعد التحقق لدى المزوّد' },
      merchants: { manage: 'ربط التجّار وضبط بيانات اعتمادهم وقواعد الاسترداد' },
    },
    analytics: { read: 'عرض التحليلات والتقارير' },
    billing: {
      read: 'عرض الخطة والاستخدام والفواتير',
      manage: 'إدارة الخطة والفواتير والدفع',
    },
    api_keys: {
      read: 'عرض مفاتيح واجهة البرمجة وآخر استخدام لها',
      manage: 'إنشاء مفاتيح واجهة البرمجة وإلغاؤها',
    },
    webhooks: {
      read: 'عرض خطافات الويب وسجل تسليمها',
      manage: 'إعداد خطافات الويب الصادرة',
    },
    network: { manage: 'اختيار المضيفات الخارجية التي يمكن لـ Hamnivo الاتصال بها' },
    mcp: {
      read: 'اسمح لمساعدي الذكاء الاصطناعي مثل Claude بقراءة المحادثات والمعرفة وذاكرة العملاء عبر خادم MCP في Hamnivo',
    },
    integrations: { manage: 'ربط القنوات وخدمات الجهات الخارجية' },
    secrets: { manage: 'تخزين بيانات الاعتماد وتدويرها' },
    extensions: { manage: 'تثبيت الإضافات وإزالتها' },
    migration: {
      review: 'عرض عمليات الاستيراد وتقاريرها ونماذج المحادثات',
      run: 'رفع ملفات التصدير وتشغيل الاستيراد',
      manage: 'اعتماد الاستيراد أو التراجع عنه',
    },
    settings: { manage: 'تعديل إعدادات مساحة العمل' },
    security: { manage: 'إعداد تسجيل الدخول والدخول الموحّد وقواعد الجلسات' },
    privacy: { manage: 'اختيار مدة الاحتفاظ بالمحادثات قبل حذفها' },
    customers: {
      export: 'تنزيل كل ما يخص عميلًا واحدًا، لطلب الاطلاع على البيانات',
      erase: 'محو عميل واحد من كل مكان، بما في ذلك Chatwoot، لطلب المحو',
    },
    support: { grant: 'السماح لدعم Hamnivo بالوصول إلى مساحة العمل هذه' },
  },
};
